/*
 * Copyright (c) Microsoft Corporation.
 * Licensed under the MIT License.
 *
 * Code generated by Microsoft (R) AutoRest Code Generator.
 * Changes may cause incorrect behavior and will be lost if the code is regenerated.
 */

import * as coreClient from "@azure/core-client";

export type InputSchemaMappingUnion =
  | InputSchemaMapping
  | JsonInputSchemaMapping;
export type DeliveryAttributeMappingUnion =
  | DeliveryAttributeMapping
  | StaticDeliveryAttributeMapping
  | DynamicDeliveryAttributeMapping;
export type DeadLetterDestinationUnion =
  | DeadLetterDestination
  | StorageBlobDeadLetterDestination;
export type EventSubscriptionDestinationUnion =
  | EventSubscriptionDestination
  | WebHookEventSubscriptionDestination
  | EventHubEventSubscriptionDestination
  | StorageQueueEventSubscriptionDestination
  | HybridConnectionEventSubscriptionDestination
  | ServiceBusQueueEventSubscriptionDestination
  | ServiceBusTopicEventSubscriptionDestination
  | AzureFunctionEventSubscriptionDestination
  | MonitorAlertEventSubscriptionDestination
  | NamespaceTopicEventSubscriptionDestination;
export type FilterUnion =
  | Filter
  | NumberInFilter
  | NumberNotInFilter
  | NumberLessThanFilter
  | NumberGreaterThanFilter
  | NumberLessThanOrEqualsFilter
  | NumberGreaterThanOrEqualsFilter
  | BoolEqualsFilter
  | StringInFilter
  | StringNotInFilter
  | StringBeginsWithFilter
  | StringEndsWithFilter
  | StringContainsFilter
  | NumberInRangeFilter
  | NumberNotInRangeFilter
  | StringNotBeginsWithFilter
  | StringNotEndsWithFilter
  | StringNotContainsFilter
  | IsNullOrUndefinedFilter
  | IsNotNullFilter;
export type AdvancedFilterUnion =
  | AdvancedFilter
  | NumberInAdvancedFilter
  | NumberNotInAdvancedFilter
  | NumberLessThanAdvancedFilter
  | NumberGreaterThanAdvancedFilter
  | NumberLessThanOrEqualsAdvancedFilter
  | NumberGreaterThanOrEqualsAdvancedFilter
  | BoolEqualsAdvancedFilter
  | StringInAdvancedFilter
  | StringNotInAdvancedFilter
  | StringBeginsWithAdvancedFilter
  | StringEndsWithAdvancedFilter
  | StringContainsAdvancedFilter
  | NumberInRangeAdvancedFilter
  | NumberNotInRangeAdvancedFilter
  | StringNotBeginsWithAdvancedFilter
  | StringNotEndsWithAdvancedFilter
  | StringNotContainsAdvancedFilter
  | IsNullOrUndefinedAdvancedFilter
  | IsNotNullAdvancedFilter;
export type StaticRoutingEnrichmentUnion =
  | StaticRoutingEnrichment
  | StaticStringRoutingEnrichment;

/** Metadata pertaining to creation and last modification of the resource. */
export interface SystemData {
  /** The identity that created the resource. */
  createdBy?: string;
  /** The type of identity that created the resource. */
  createdByType?: CreatedByType;
  /** The timestamp of resource creation (UTC). */
  createdAt?: Date;
  /** The identity that last modified the resource. */
  lastModifiedBy?: string;
  /** The type of identity that last modified the resource. */
  lastModifiedByType?: CreatedByType;
  /** The timestamp of resource last modification (UTC) */
  lastModifiedAt?: Date;
}

/** Definition of a Resource. */
export interface Resource {
  /**
   * Fully qualified identifier of the resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly id?: string;
  /**
   * Name of the resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly name?: string;
  /**
   * Type of the resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly type?: string;
}

/** Common error response for all Azure Resource Manager APIs to return error details for failed operations. (This also follows the OData error response format.). */
export interface ErrorResponse {
  /** The error object. */
  error?: ErrorDetail;
}

/** The error detail. */
export interface ErrorDetail {
  /**
   * The error code.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly code?: string;
  /**
   * The error message.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly message?: string;
  /**
   * The error target.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly target?: string;
  /**
   * The error details.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly details?: ErrorDetail[];
  /**
   * The error additional info.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly additionalInfo?: ErrorAdditionalInfo[];
}

/** The resource management error additional info. */
export interface ErrorAdditionalInfo {
  /**
   * The additional info type.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly type?: string;
  /**
   * The additional info.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly info?: Record<string, unknown>;
}

/** Result of the List CA Certificate operation. */
export interface CaCertificatesListResult {
  /** A collection of CA Certificate. */
  value?: CaCertificate[];
  /** A link for the next page of CA Certificate. */
  nextLink?: string;
}

/** Properties of the corresponding partner topic of a Channel. */
export interface PartnerTopicInfo {
  /**
   * Azure subscription ID of the subscriber. The partner topic associated with the channel will be
   * created under this Azure subscription.
   */
  azureSubscriptionId?: string;
  /**
   * Azure Resource Group of the subscriber. The partner topic associated with the channel will be
   * created under this resource group.
   */
  resourceGroupName?: string;
  /** Name of the partner topic associated with the channel. */
  name?: string;
  /**
   * Event Type Information for the partner topic. This information is provided by the publisher and can be used by the
   * subscriber to view different types of events that are published.
   */
  eventTypeInfo?: EventTypeInfo;
  /**
   * The source information is provided by the publisher to determine the scope or context from which the events
   * are originating. This information can be used by the subscriber during the approval process of the
   * created partner topic.
   */
  source?: string;
}

/** The event type information for Channels. */
export interface EventTypeInfo {
  /** The kind of event type used. */
  kind?: EventDefinitionKind;
  /**
   * A collection of inline event types for the resource. The inline event type keys are of type string which represents the name of the event.
   * An example of a valid inline event name is "Contoso.OrderCreated".
   * The inline event type values are of type InlineEventProperties and will contain additional information for every inline event type.
   */
  inlineEventTypes?: { [propertyName: string]: InlineEventProperties };
}

/** Additional information about every inline event. */
export interface InlineEventProperties {
  /** The description for the inline event. */
  description?: string;
  /** The displayName for the inline event. */
  displayName?: string;
  /** The documentationUrl for the inline event. */
  documentationUrl?: string;
  /** The dataSchemaUrl for the inline event. */
  dataSchemaUrl?: string;
}

/** Properties of the Channel update. */
export interface ChannelUpdateParameters {
  /**
   * Expiration time of the channel. If this timer expires while the corresponding partner topic or partner destination is never activated,
   * the channel and corresponding partner topic or partner destination are deleted.
   */
  expirationTimeIfNotActivatedUtc?: Date;
  /** Partner topic properties which can be updated if the channel is of type PartnerTopic. */
  partnerTopicInfo?: PartnerUpdateTopicInfo;
}

/** Update properties for the corresponding partner topic of a channel. */
export interface PartnerUpdateTopicInfo {
  /** Event type info for the partner topic */
  eventTypeInfo?: EventTypeInfo;
}

/** Result of the List Channels operation */
export interface ChannelsListResult {
  /** A collection of Channels. */
  value?: Channel[];
  /** A link for the next page of channels. */
  nextLink?: string;
}

/** Full endpoint URL of an event subscription */
export interface EventSubscriptionFullUrl {
  /** The URL that represents the endpoint of the destination of an event subscription. */
  endpointUrl?: string;
}

/** Result of the List Client Group operation. */
export interface ClientGroupsListResult {
  /** A collection of Client Group. */
  value?: ClientGroup[];
  /** A link for the next page of Client Group. */
  nextLink?: string;
}

/** The certificate authentication properties for the client. */
export interface ClientCertificateAuthentication {
  /** The validation scheme used to authenticate the client. Default value is SubjectMatchesAuthenticationName. */
  validationScheme?: ClientCertificateValidationScheme;
  /** The list of thumbprints that are allowed during client authentication. This property is required only if the validationScheme is 'ThumbprintMatch'. */
  allowedThumbprints?: string[];
}

/** Result of the List Client operation. */
export interface ClientsListResult {
  /** A collection of Client. */
  value?: Client[];
  /** A link for the next page of Client. */
  nextLink?: string;
}

/** PrivateEndpoint information. */
export interface PrivateEndpoint {
  /** The ARM identifier for Private Endpoint. */
  id?: string;
}

/** ConnectionState information. */
export interface ConnectionState {
  /** Status of the connection. */
  status?: PersistedConnectionStatus;
  /** Description of the connection state. */
  description?: string;
  /** Actions required (if any). */
  actionsRequired?: string;
}

/** By default, Event Grid expects events to be in the Event Grid event schema. Specifying an input schema mapping enables publishing to Event Grid using a custom input schema. Currently, the only supported type of InputSchemaMapping is 'JsonInputSchemaMapping'. */
export interface InputSchemaMapping {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  inputSchemaMappingType: "Json";
}

export interface InboundIpRule {
  /** IP Address in CIDR notation e.g., 10.0.0.0/8. */
  ipMask?: string;
  /** Action to perform based on the match or no match of the IpMask. */
  action?: IpActionType;
}

/** The identity information for the resource. */
export interface IdentityInfo {
  /** The type of managed identity used. The type 'SystemAssigned, UserAssigned' includes both an implicitly created identity and a set of user-assigned identities. The type 'None' will remove any identity. */
  type?: IdentityType;
  /** The principal ID of resource identity. */
  principalId?: string;
  /** The tenant ID of resource. */
  tenantId?: string;
  /**
   * The list of user identities associated with the resource. The user identity dictionary key references will be ARM resource ids in the form:
   * '/subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.ManagedIdentity/userAssignedIdentities/{identityName}'.
   * This property is currently not used and reserved for future usage.
   */
  userAssignedIdentities?: { [propertyName: string]: UserIdentityProperties };
}

/** The information about the user identity. */
export interface UserIdentityProperties {
  /** The principal id of user assigned identity. */
  principalId?: string;
  /** The client id of user assigned identity. */
  clientId?: string;
}

/** Properties of the Domain update. */
export interface DomainUpdateParameters {
  /** Tags of the domains resource. */
  tags?: { [propertyName: string]: string };
  /** Identity information for the resource. */
  identity?: IdentityInfo;
  /**
   * This determines if traffic is allowed over public network. By default it is enabled.
   * You can further restrict to specific IPs by configuring <seealso cref="P:Microsoft.Azure.Events.ResourceProvider.Common.Contracts.DomainUpdateParameterProperties.InboundIpRules" />
   */
  publicNetworkAccess?: PublicNetworkAccess;
  /** This can be used to restrict traffic from specific IPs instead of all IPs. Note: These are considered only if PublicNetworkAccess is enabled. */
  inboundIpRules?: InboundIpRule[];
  /** Minimum TLS version of the publisher allowed to publish to this domain */
  minimumTlsVersionAllowed?: TlsVersion;
  /** This boolean is used to enable or disable local auth. Default value is false. When the property is set to true, only AAD token will be used to authenticate if user is allowed to publish to the domain. */
  disableLocalAuth?: boolean;
  /**
   * This Boolean is used to specify the creation mechanism for 'all' the Event Grid Domain Topics associated with this Event Grid Domain resource.
   * In this context, creation of domain topic can be auto-managed (when true) or self-managed (when false). The default value for this property is true.
   * When this property is null or set to true, Event Grid is responsible of automatically creating the domain topic when the first event subscription is
   * created at the scope of the domain topic. If this property is set to false, then creating the first event subscription will require creating a domain topic
   * by the user. The self-management mode can be used if the user wants full control of when the domain topic is created, while auto-managed mode provides the
   * flexibility to perform less operations and manage fewer resources by the user. Also, note that in auto-managed creation mode, user is allowed to create the
   * domain topic on demand if needed.
   */
  autoCreateTopicWithFirstSubscription?: boolean;
  /**
   * This Boolean is used to specify the deletion mechanism for 'all' the Event Grid Domain Topics associated with this Event Grid Domain resource.
   * In this context, deletion of domain topic can be auto-managed (when true) or self-managed (when false). The default value for this property is true.
   * When this property is set to true, Event Grid is responsible of automatically deleting the domain topic when the last event subscription at the scope
   * of the domain topic is deleted. If this property is set to false, then the user needs to manually delete the domain topic when it is no longer needed
   * (e.g., when last event subscription is deleted and the resource needs to be cleaned up). The self-management mode can be used if the user wants full
   * control of when the domain topic needs to be deleted, while auto-managed mode provides the flexibility to perform less operations and manage fewer
   * resources by the user.
   */
  autoDeleteTopicWithLastSubscription?: boolean;
  /** The data residency boundary for the domain. */
  dataResidencyBoundary?: DataResidencyBoundary;
  /** The eventTypeInfo for the domain. */
  eventTypeInfo?: EventTypeInfo;
}

/** Result of the List Domains operation. */
export interface DomainsListResult {
  /** A collection of Domains. */
  value?: Domain[];
  /** A link for the next page of domains. */
  nextLink?: string;
}

/** Shared access keys of the Domain. */
export interface DomainSharedAccessKeys {
  /** Shared access key1 for the domain. */
  key1?: string;
  /** Shared access key2 for the domain. */
  key2?: string;
}

/** Domain regenerate share access key request. */
export interface DomainRegenerateKeyRequest {
  /** Key name to regenerate key1 or key2. */
  keyName: string;
}

/** Result of the List Domain Topics operation. */
export interface DomainTopicsListResult {
  /** A collection of Domain Topics. */
  value?: DomainTopic[];
  /** A link for the next page of domain topics. */
  nextLink?: string;
}

/** Result of the Get delivery attributes operation. */
export interface DeliveryAttributeListResult {
  /** A collection of DeliveryAttributeMapping */
  value?: DeliveryAttributeMappingUnion[];
}

/** Delivery attribute mapping details. */
export interface DeliveryAttributeMapping {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  type: "Static" | "Dynamic";
  /** Name of the delivery attribute or header. */
  name?: string;
}

/** Properties of the delivery configuration information of the event subscription. */
export interface DeliveryConfiguration {
  /** Delivery mode of the event subscription. */
  deliveryMode?: DeliveryMode;
  /** This property should be populated when deliveryMode is queue and represents information about the queue subscription. */
  queue?: QueueInfo;
  /** This property should be populated when deliveryMode is push and represents information about the push subscription. */
  push?: PushInfo;
}

/** Properties of the Queue info for event subscription. */
export interface QueueInfo {
  /**
   * Maximum period in seconds in which once the message is in received (by the client) state and waiting to be accepted, released or rejected.
   * If this time elapsed after a message has been received by the client and not transitioned into accepted (not processed), released or rejected,
   * the message is available for redelivery. This is an optional field, where default is 60 seconds, minimum is 60 seconds and maximum is 300 seconds.
   */
  receiveLockDurationInSeconds?: number;
  /** The maximum delivery count of the events. */
  maxDeliveryCount?: number;
  /**
   * The dead letter destination of the event subscription. Any event that cannot be delivered to its' destination is sent to the dead letter destination.
   * Uses the managed identity setup on the parent resource (namely, topic) to acquire the authentication tokens being used during delivery / dead-lettering.
   */
  deadLetterDestinationWithResourceIdentity?: DeadLetterWithResourceIdentity;
  /**
   * Time span duration in ISO 8601 format that determines how long messages are available to the subscription from the time the message was published.
   * This duration value is expressed using the following format: \'P(n)Y(n)M(n)DT(n)H(n)M(n)S\', where:
   *     - (n) is replaced by the value of each time element that follows the (n).
   *     - P is the duration (or Period) designator and is always placed at the beginning of the duration.
   *     - Y is the year designator, and it follows the value for the number of years.
   *     - M is the month designator, and it follows the value for the number of months.
   *     - W is the week designator, and it follows the value for the number of weeks.
   *     - D is the day designator, and it follows the value for the number of days.
   *     - T is the time designator, and it precedes the time components.
   *     - H is the hour designator, and it follows the value for the number of hours.
   *     - M is the minute designator, and it follows the value for the number of minutes.
   *     - S is the second designator, and it follows the value for the number of seconds.
   * This duration value cannot be set greater than the topic’s EventRetentionInDays. It is is an optional field where its minimum value is 1 minute, and its maximum is determined
   * by topic’s EventRetentionInDays value. The followings are examples of valid values:
   *     - \'P0DT23H12M\' or \'PT23H12M\': for duration of 23 hours and 12 minutes.
   *     - \'P1D\' or \'P1DT0H0M0S\': for duration of 1 day.
   */
  eventTimeToLive?: string;
}

/** Information about the deadletter destination with resource identity. */
export interface DeadLetterWithResourceIdentity {
  /** The identity to use when dead-lettering events. */
  identity?: EventSubscriptionIdentity;
  /**
   * Information about the destination where events have to be delivered for the event subscription.
   * Uses the managed identity setup on the parent resource (namely, topic or domain) to acquire the authentication tokens being used during dead-lettering.
   */
  deadLetterDestination?: DeadLetterDestinationUnion;
}

/** The identity information with the event subscription. */
export interface EventSubscriptionIdentity {
  /** The type of managed identity used. Can be either 'SystemAssigned' or 'UserAssigned'. */
  type?: EventSubscriptionIdentityType;
  /** The user identity associated with the resource. */
  userAssignedIdentity?: string;
}

/** Information about the dead letter destination for an event subscription. To configure a deadletter destination, do not directly instantiate an object of this class. Instead, instantiate an object of a derived class. Currently, StorageBlobDeadLetterDestination is the only class that derives from this class. */
export interface DeadLetterDestination {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  endpointType: "StorageBlob";
}

/** Properties of the destination info for event subscription supporting push. */
export interface PushInfo {
  /** The maximum delivery count of the events. */
  maxDeliveryCount?: number;
  /**
   * Time span duration in ISO 8601 format that determines how long messages are available to the subscription from the time the message was published.
   * This duration value is expressed using the following format: \'P(n)Y(n)M(n)DT(n)H(n)M(n)S\', where:
   *     - (n) is replaced by the value of each time element that follows the (n).
   *     - P is the duration (or Period) designator and is always placed at the beginning of the duration.
   *     - Y is the year designator, and it follows the value for the number of years.
   *     - M is the month designator, and it follows the value for the number of months.
   *     - W is the week designator, and it follows the value for the number of weeks.
   *     - D is the day designator, and it follows the value for the number of days.
   *     - T is the time designator, and it precedes the time components.
   *     - H is the hour designator, and it follows the value for the number of hours.
   *     - M is the minute designator, and it follows the value for the number of minutes.
   *     - S is the second designator, and it follows the value for the number of seconds.
   * This duration value cannot be set greater than the topic’s EventRetentionInDays. It is is an optional field where its minimum value is 1 minute, and its maximum is determined
   * by topic’s EventRetentionInDays value. The followings are examples of valid values:
   *     - \'P0DT23H12M\' or \'PT23H12M\': for duration of 23 hours and 12 minutes.
   *     - \'P1D\' or \'P1DT0H0M0S\': for duration of 1 day.
   */
  eventTimeToLive?: string;
  /**
   * The dead letter destination of the event subscription. Any event that cannot be delivered to its' destination is sent to the dead letter destination.
   * Uses the managed identity setup on the parent resource (namely, namespace) to acquire the authentication tokens being used during dead-lettering.
   */
  deadLetterDestinationWithResourceIdentity?: DeadLetterWithResourceIdentity;
  /**
   * Information about the destination where events have to be delivered for the event subscription.
   * Uses the managed identity setup on the parent resource (namely, topic or domain) to acquire the authentication tokens being used during delivery.
   */
  deliveryWithResourceIdentity?: DeliveryWithResourceIdentity;
  /**
   * Information about the destination where events have to be delivered for the event subscription.
   * Uses Azure Event Grid's identity to acquire the authentication tokens being used during delivery.
   */
  destination?: EventSubscriptionDestinationUnion;
}

/** Information about the delivery for an event subscription with resource identity. */
export interface DeliveryWithResourceIdentity {
  /** The identity to use when delivering events. */
  identity?: EventSubscriptionIdentity;
  /**
   * Information about the destination where events have to be delivered for the event subscription.
   * Uses the managed identity setup on the parent resource (namely, topic or domain) to acquire the authentication tokens being used during delivery.
   */
  destination?: EventSubscriptionDestinationUnion;
}

/** Information about the destination for an event subscription. */
export interface EventSubscriptionDestination {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  endpointType:
    | "WebHook"
    | "EventHub"
    | "StorageQueue"
    | "HybridConnection"
    | "ServiceBusQueue"
    | "ServiceBusTopic"
    | "AzureFunction"
    | "MonitorAlert"
    | "NamespaceTopic";
}

/** Filters configuration for the Event Subscription. */
export interface FiltersConfiguration {
  /** A list of applicable event types that need to be part of the event subscription. If it is desired to subscribe to all default event types, set the IncludedEventTypes to null. */
  includedEventTypes?: string[];
  /** An array of filters that are used for filtering event subscriptions. */
  filters?: FilterUnion[];
}

/**
 * This is the base type that represents a filter. To configure a filter, do not directly instantiate an object of this class. Instead, instantiate
 * an object of a derived class such as BoolEqualsFilter, NumberInFilter etc depending on the type of the key based on
 * which you want to filter.
 */
export interface Filter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType:
    | "NumberIn"
    | "NumberNotIn"
    | "NumberLessThan"
    | "NumberGreaterThan"
    | "NumberLessThanOrEquals"
    | "NumberGreaterThanOrEquals"
    | "BoolEquals"
    | "StringIn"
    | "StringNotIn"
    | "StringBeginsWith"
    | "StringEndsWith"
    | "StringContains"
    | "NumberInRange"
    | "NumberNotInRange"
    | "StringNotBeginsWith"
    | "StringNotEndsWith"
    | "StringNotContains"
    | "IsNullOrUndefined"
    | "IsNotNull";
  /** The field/property in the event based on which you want to filter. */
  key?: string;
}

/** Properties of the Event Subscription update. */
export interface SubscriptionUpdateParameters {
  /** Information about the delivery configuration of the event subscription. */
  deliveryConfiguration?: DeliveryConfiguration;
  /** The event delivery schema for the event subscription. */
  eventDeliverySchema?: DeliverySchema;
  /** Information about the filter for the event subscription. */
  filtersConfiguration?: FiltersConfiguration;
  /** Expiration time of the event subscription. */
  expirationTimeUtc?: Date;
}

/** Result of the List event subscriptions operation. */
export interface SubscriptionsListResult {
  /** A collection of Subscriptions. */
  value?: Subscription[];
  /** A link for the next page of event subscriptions */
  nextLink?: string;
}

/** Filter for the Event Subscription. */
export interface EventSubscriptionFilter {
  /**
   * An optional string to filter events for an event subscription based on a resource path prefix.
   * The format of this depends on the publisher of the events.
   * Wildcard characters are not supported in this path.
   */
  subjectBeginsWith?: string;
  /**
   * An optional string to filter events for an event subscription based on a resource path suffix.
   * Wildcard characters are not supported in this path.
   */
  subjectEndsWith?: string;
  /** A list of applicable event types that need to be part of the event subscription. If it is desired to subscribe to all default event types, set the IncludedEventTypes to null. */
  includedEventTypes?: string[];
  /**
   * Specifies if the SubjectBeginsWith and SubjectEndsWith properties of the filter
   * should be compared in a case sensitive manner.
   */
  isSubjectCaseSensitive?: boolean;
  /** Allows advanced filters to be evaluated against an array of values instead of expecting a singular value. */
  enableAdvancedFilteringOnArrays?: boolean;
  /** An array of advanced filters that are used for filtering event subscriptions. */
  advancedFilters?: AdvancedFilterUnion[];
}

/** This is the base type that represents an advanced filter. To configure an advanced filter, do not directly instantiate an object of this class. Instead, instantiate an object of a derived class such as BoolEqualsAdvancedFilter, NumberInAdvancedFilter, StringEqualsAdvancedFilter etc. depending on the type of the key based on which you want to filter. */
export interface AdvancedFilter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType:
    | "NumberIn"
    | "NumberNotIn"
    | "NumberLessThan"
    | "NumberGreaterThan"
    | "NumberLessThanOrEquals"
    | "NumberGreaterThanOrEquals"
    | "BoolEquals"
    | "StringIn"
    | "StringNotIn"
    | "StringBeginsWith"
    | "StringEndsWith"
    | "StringContains"
    | "NumberInRange"
    | "NumberNotInRange"
    | "StringNotBeginsWith"
    | "StringNotEndsWith"
    | "StringNotContains"
    | "IsNullOrUndefined"
    | "IsNotNull";
  /** The field/property in the event based on which you want to filter. */
  key?: string;
}

/** Information about the retry policy for an event subscription. */
export interface RetryPolicy {
  /** Maximum number of delivery retry attempts for events. */
  maxDeliveryAttempts?: number;
  /** Time To Live (in minutes) for events. */
  eventTimeToLiveInMinutes?: number;
}

/** Properties of the Event Subscription update. */
export interface EventSubscriptionUpdateParameters {
  /**
   * Information about the destination where events have to be delivered for the event subscription.
   * Uses Azure Event Grid's identity to acquire the authentication tokens being used during delivery / dead-lettering.
   */
  destination?: EventSubscriptionDestinationUnion;
  /**
   * Information about the destination where events have to be delivered for the event subscription.
   * Uses the managed identity setup on the parent resource (topic / domain) to acquire the authentication tokens being used during delivery / dead-lettering.
   */
  deliveryWithResourceIdentity?: DeliveryWithResourceIdentity;
  /** Information about the filter for the event subscription. */
  filter?: EventSubscriptionFilter;
  /** List of user defined labels. */
  labels?: string[];
  /** Information about the expiration time for the event subscription. */
  expirationTimeUtc?: Date;
  /** The event delivery schema for the event subscription. */
  eventDeliverySchema?: EventDeliverySchema;
  /** The retry policy for events. This can be used to configure maximum number of delivery attempts and time to live for events. */
  retryPolicy?: RetryPolicy;
  /**
   * The dead letter destination of the event subscription. Any event that cannot be delivered to its' destination is sent to the dead letter destination.
   * Uses Azure Event Grid's identity to acquire the authentication tokens being used during delivery / dead-lettering.
   */
  deadLetterDestination?: DeadLetterDestinationUnion;
  /**
   * The dead letter destination of the event subscription. Any event that cannot be delivered to its' destination is sent to the dead letter destination.
   * Uses the managed identity setup on the parent resource (topic / domain) to acquire the authentication tokens being used during delivery / dead-lettering.
   */
  deadLetterWithResourceIdentity?: DeadLetterWithResourceIdentity;
}

/** Result of the List EventSubscriptions operation */
export interface EventSubscriptionsListResult {
  /** A collection of EventSubscriptions */
  value?: EventSubscription[];
  /** A link for the next page of event subscriptions */
  nextLink?: string;
}

/** Properties of the Topics Configuration. */
export interface TopicsConfiguration {
  /**
   * The hostname for the topics configuration. This is a read-only property.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly hostname?: string;
  /** List of custom domain configurations for the namespace. */
  customDomains?: CustomDomainConfiguration[];
}

/** A custom domain configuration that allows users to publish to their own domain name. */
export interface CustomDomainConfiguration {
  /** Fully Qualified Domain Name (FQDN) for the custom domain. */
  fullyQualifiedDomainName: string;
  /** Validation state for the custom domain. This is a read only property and is initially set to 'Pending' and will be updated to 'Approved' by Event Grid only after ownership of the domain name has been successfully validated. */
  validationState?: CustomDomainValidationState;
  /** Identity info for accessing the certificate for the custom domain. This identity info must match an identity that has been set on the namespace. */
  identity?: CustomDomainIdentity;
  /**
   * The URL for the certificate that is used for publishing to the custom domain. We currently support certificates stored in Azure Key Vault only. While certificate URL can be either
   * versioned URL of the following format https://{key-vault-name}.vault.azure.net/certificates/{certificate-name}/{version-id}, or unversioned URL of the following format (e.g.,
   * https://contosovault.vault.azure.net/certificates/contosocert, we support unversioned certificate URL only (e.g., https://contosovault.vault.azure.net/certificates/contosocert)
   */
  certificateUrl?: string;
  /**
   * Expected DNS TXT record name. Event Grid will check for a TXT record with this name in the DNS record set of the custom domain name to prove ownership over the domain.
   * The values under this TXT record must contain the expected TXT record value.
   */
  expectedTxtRecordName?: string;
  /** Expected DNS TXT record value. Event Grid will check for a TXT record with this value in the DNS record set of the custom domain name to prove ownership over the domain. */
  expectedTxtRecordValue?: string;
}

/** The identity information for retrieving the certificate for the custom domain. */
export interface CustomDomainIdentity {
  /** The type of managed identity used. Can be either 'SystemAssigned' or 'UserAssigned'. */
  type?: CustomDomainIdentityType;
  /** The user identity associated with the resource. */
  userAssignedIdentity?: string;
}

/** Properties of the Topic Spaces Configuration. */
export interface TopicSpacesConfiguration {
  /** Indicate if Topic Spaces Configuration is enabled for the namespace. Default is Disabled. */
  state?: TopicSpacesConfigurationState;
  /**
   * Fully qualified Azure Resource Id for the Event Grid Topic to which events will be routed to from TopicSpaces under a namespace.
   * This property should be in the following format '/subscriptions/{subId}/resourcegroups/{resourceGroupName}/providers/microsoft.EventGrid/topics/{topicName}'.
   * This topic should reside in the same region where namespace is located.
   */
  routeTopicResourceId?: string;
  /**
   * The endpoint for the topic spaces configuration. This is a read-only property.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly hostname?: string;
  /** Routing enrichments for topic spaces configuration */
  routingEnrichments?: RoutingEnrichments;
  /**
   * The maximum session expiry in hours. The property default value is 1 hour.
   * Min allowed value is 1 hour and max allowed value is 8 hours.
   */
  maximumSessionExpiryInHours?: number;
  /**
   * The maximum number of sessions per authentication name. The property default value is 1.
   * Min allowed value is 1 and max allowed value is 100.
   */
  maximumClientSessionsPerAuthenticationName?: number;
  /** Routing identity info for topic spaces configuration. */
  routingIdentityInfo?: RoutingIdentityInfo;
  /** List of custom domain configurations for the namespace. */
  customDomains?: CustomDomainConfiguration[];
}

export interface RoutingEnrichments {
  static?: StaticRoutingEnrichmentUnion[];
  dynamic?: DynamicRoutingEnrichment[];
}

/** Static routing enrichment details. */
export interface StaticRoutingEnrichment {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  valueType: "String";
  /** Static routing enrichment key. */
  key?: string;
}

export interface DynamicRoutingEnrichment {
  /** Dynamic routing enrichment key. */
  key?: string;
  /** Dynamic routing enrichment value. */
  value?: string;
}

/** Routing identity info for topic spaces configuration. */
export interface RoutingIdentityInfo {
  /** Routing identity type for topic spaces configuration. */
  type?: RoutingIdentityType;
  userAssignedIdentity?: string;
}

/** Represents available Sku pricing tiers. */
export interface NamespaceSku {
  /** The name of the SKU. */
  name?: SkuName;
  /**
   * Specifies the number of Throughput Units that defines the capacity for the namespace. The property default value is
   * 1 which signifies 1 Throughput Unit = 1MB/s ingress and 2MB/s egress per namespace. Min capacity is 1 and
   * max allowed capacity is 20.
   */
  capacity?: number;
}

/** Properties to update namespace. */
export interface NamespaceUpdateParameters {
  /** Tags of the namespace resource. */
  tags?: { [propertyName: string]: string };
  /** Namespace resource identity information. */
  identity?: IdentityInfo;
  /** Represents available Sku pricing tiers. */
  sku?: NamespaceSku;
  /** Topic spaces configuration properties that can be updated. */
  topicSpacesConfiguration?: UpdateTopicSpacesConfigurationInfo;
  /** Topics configuration properties that can be updated. */
  topicsConfiguration?: UpdateTopicsConfigurationInfo;
  /**
   * This determines if traffic is allowed over public network. By default it is enabled.
   * You can further restrict to specific IPs by configuring <seealso cref="P:Microsoft.Azure.Events.ResourceProvider.Common.Contracts.PubSub.NamespaceUpdateParameterProperties.InboundIpRules" />
   */
  publicNetworkAccess?: PublicNetworkAccess;
  /** This can be used to restrict traffic from specific IPs instead of all IPs. Note: These are considered only if PublicNetworkAccess is enabled. */
  inboundIpRules?: InboundIpRule[];
}

/** Properties of the topic spaces configuration info of a namespace. */
export interface UpdateTopicSpacesConfigurationInfo {
  /** Indicate if Topic Spaces Configuration is enabled for the namespace. Default is Disabled. */
  state?: TopicSpacesConfigurationState;
  /** This property is used to specify custom topic to which events will be routed to from topic spaces configuration under namespace. */
  routeTopicResourceId?: string;
  /** Routing enrichments for topic spaces configuration. */
  routingEnrichments?: RoutingEnrichments;
  /**
   * The maximum session expiry in hours. The property default value is 1 hour.
   * Min allowed value is 1 hour and max allowed value is 8 hours.
   */
  maximumSessionExpiryInHours?: number;
  /**
   * The maximum number of sessions per authentication name. The property default value is 1.
   * Min allowed value is 1 and max allowed value is 100.
   */
  maximumClientSessionsPerAuthenticationName?: number;
  /** Routing identity info for topic spaces configuration. */
  routingIdentityInfo?: RoutingIdentityInfo;
  /** Custom domain info for topic spaces configuration. */
  customDomains?: CustomDomainConfiguration[];
}

/** Properties of the topics configuration info of a namespace. */
export interface UpdateTopicsConfigurationInfo {
  /** Custom domain info for topics configuration. */
  customDomains?: CustomDomainConfiguration[];
}

/** Result of the List Namespaces operation. */
export interface NamespacesListResult {
  /** A collection of namespaces. */
  value?: Namespace[];
  /** A link for the next page of namespaces. */
  nextLink?: string;
}

/** Shared access keys of the Namespace. */
export interface NamespaceSharedAccessKeys {
  /** Shared access key1 for the namespace. */
  key1?: string;
  /** Shared access key2 for the namespace. */
  key2?: string;
}

/** Namespace regenerate share access key request. */
export interface NamespaceRegenerateKeyRequest {
  /** Key name to regenerate key1 or key2. */
  keyName: string;
}

/** Properties of the namespace topic update. */
export interface NamespaceTopicUpdateParameters {
  /**
   * Event retention for the namespace topic expressed in days. The property default value is 1 day.
   * Min event retention duration value is 1 day and max event retention duration value is 1 day.
   */
  eventRetentionInDays?: number;
}

/** Result of the List namespace topics operation. */
export interface NamespaceTopicsListResult {
  /** A collection of namespace topics. */
  value?: NamespaceTopic[];
  /** A link for the next page of namespace topics. */
  nextLink?: string;
}

/** Shared access keys of the Topic */
export interface TopicSharedAccessKeys {
  /** Shared access key1 for the topic. */
  key1?: string;
  /** Shared access key2 for the topic. */
  key2?: string;
}

/** Topic regenerate share access key request */
export interface TopicRegenerateKeyRequest {
  /** Key name to regenerate key1 or key2 */
  keyName: string;
}

/** Result of the List Operations operation */
export interface OperationsListResult {
  /** A collection of operations */
  value?: Operation[];
}

/** Represents an operation returned by the GetOperations request. */
export interface Operation {
  /** Name of the operation. */
  name?: string;
  /** Display name of the operation. */
  display?: OperationInfo;
  /** Origin of the operation. */
  origin?: string;
  /** This Boolean is used to determine if the operation is a data plane action or not. */
  isDataAction?: boolean;
  /** Properties of the operation. */
  properties?: Record<string, unknown>;
}

/** Information about an operation */
export interface OperationInfo {
  /** Name of the provider */
  provider?: string;
  /** Name of the resource type */
  resource?: string;
  /** Name of the operation */
  operation?: string;
  /** Description of the operation */
  description?: string;
}

/** Properties of the Topic update */
export interface TopicUpdateParameters {
  /** Tags of the Topic resource. */
  tags?: { [propertyName: string]: string };
  /** Topic resource identity information. */
  identity?: IdentityInfo;
  /**
   * This determines if traffic is allowed over public network. By default it is enabled.
   * You can further restrict to specific IPs by configuring <seealso cref="P:Microsoft.Azure.Events.ResourceProvider.Common.Contracts.TopicUpdateParameterProperties.InboundIpRules" />
   */
  publicNetworkAccess?: PublicNetworkAccess;
  /** This can be used to restrict traffic from specific IPs instead of all IPs. Note: These are considered only if PublicNetworkAccess is enabled. */
  inboundIpRules?: InboundIpRule[];
  /** Minimum TLS version of the publisher allowed to publish to this domain */
  minimumTlsVersionAllowed?: TlsVersion;
  /** This boolean is used to enable or disable local auth. Default value is false. When the property is set to true, only AAD token will be used to authenticate if user is allowed to publish to the topic. */
  disableLocalAuth?: boolean;
  /** The data residency boundary for the topic. */
  dataResidencyBoundary?: DataResidencyBoundary;
  /** The eventTypeInfo for the topic. */
  eventTypeInfo?: EventTypeInfo;
}

/** Result of the List Topics operation */
export interface TopicsListResult {
  /** A collection of Topics */
  value?: Topic[];
  /** A link for the next page of topics */
  nextLink?: string;
}

/** The partner authorization details. */
export interface PartnerAuthorization {
  /**
   * Time used to validate the authorization expiration time for each authorized partner. If DefaultMaximumExpirationTimeInDays is
   * not specified, the default is 7 days. Otherwise, allowed values are between 1 and 365 days.
   */
  defaultMaximumExpirationTimeInDays?: number;
  /** The list of authorized partners. */
  authorizedPartnersList?: Partner[];
}

/** Information about the partner. */
export interface Partner {
  /** The immutableId of the corresponding partner registration. */
  partnerRegistrationImmutableId?: string;
  /** The partner name. */
  partnerName?: string;
  /**
   * Expiration time of the partner authorization. If this timer expires, any request from this partner to create, update or delete resources in subscriber's
   * context will fail. If specified, the allowed values are between 1 to the value of defaultMaximumExpirationTimeInDays specified in PartnerConfiguration.
   * If not specified, the default value will be the value of defaultMaximumExpirationTimeInDays specified in PartnerConfiguration or 7 if this value is not specified.
   */
  authorizationExpirationTimeInUtc?: Date;
}

/** Properties of the partner configuration update. */
export interface PartnerConfigurationUpdateParameters {
  /** Tags of the partner configuration resource. */
  tags?: { [propertyName: string]: string };
  /** The default time used to validate the maximum expiration time for each authorized partners in days. Allowed values ar between 1 and 365 days. */
  defaultMaximumExpirationTimeInDays?: number;
}

/** Result of the List partner configurations operation */
export interface PartnerConfigurationsListResult {
  /** A collection of partner configurations. */
  value?: PartnerConfiguration[];
  /** A link for the next page of partner configurations. */
  nextLink?: string;
}

/** Properties of the Partner Namespace update. */
export interface PartnerNamespaceUpdateParameters {
  /** Tags of the Partner Namespace. */
  tags?: { [propertyName: string]: string };
  /**
   * This determines if traffic is allowed over public network. By default it is enabled.
   * You can further restrict to specific IPs by configuring <seealso cref="P:Microsoft.Azure.Events.ResourceProvider.Common.Contracts.PartnerNamespaceUpdateParameterProperties.InboundIpRules" />
   */
  publicNetworkAccess?: PublicNetworkAccess;
  /** This can be used to restrict traffic from specific IPs instead of all IPs. Note: These are considered only if PublicNetworkAccess is enabled. */
  inboundIpRules?: InboundIpRule[];
  /** Minimum TLS version of the publisher allowed to publish to this domain */
  minimumTlsVersionAllowed?: TlsVersion;
  /** This boolean is used to enable or disable local auth. Default value is false. When the property is set to true, only AAD token will be used to authenticate if user is allowed to publish to the partner namespace. */
  disableLocalAuth?: boolean;
}

/** Result of the List Partner Namespaces operation */
export interface PartnerNamespacesListResult {
  /** A collection of partner namespaces. */
  value?: PartnerNamespace[];
  /** A link for the next page of partner namespaces. */
  nextLink?: string;
}

/** Shared access keys of the partner namespace. */
export interface PartnerNamespaceSharedAccessKeys {
  /** Shared access key1 for the partner namespace. */
  key1?: string;
  /** Shared access key2 for the partner namespace. */
  key2?: string;
}

/** PartnerNamespace regenerate shared access key request. */
export interface PartnerNamespaceRegenerateKeyRequest {
  /** Key name to regenerate (key1 or key2). */
  keyName: string;
}

/** Properties of the Partner Registration update. */
export interface PartnerRegistrationUpdateParameters {
  /** Tags of the partner registration resource. */
  tags?: { [propertyName: string]: string };
}

/** Result of the List Partner Registrations operation. */
export interface PartnerRegistrationsListResult {
  /** A collection of partner registrations. */
  value?: PartnerRegistration[];
  /** A link for the next page of partner registrations. */
  nextLink?: string;
}

/** Properties of the Partner Topic update. */
export interface PartnerTopicUpdateParameters {
  /** Tags of the Partner Topic resource. */
  tags?: { [propertyName: string]: string };
  /** Identity information for the Partner Topic resource. */
  identity?: IdentityInfo;
}

/** Result of the List Partner Topics operation. */
export interface PartnerTopicsListResult {
  /** A collection of partner topics. */
  value?: PartnerTopic[];
  /** A link for the next page of partner topics. */
  nextLink?: string;
}

/** Result of the List Permission Binding operation. */
export interface PermissionBindingsListResult {
  /** A collection of Permission Binding. */
  value?: PermissionBinding[];
  /** A link for the next page of Permission Binding. */
  nextLink?: string;
}

/** Result of the list of all private endpoint connections operation. */
export interface PrivateEndpointConnectionListResult {
  /** A collection of private endpoint connection resources. */
  value?: PrivateEndpointConnection[];
  /** A link for the next page of private endpoint connection resources. */
  nextLink?: string;
}

/** Information of the private link resource. */
export interface PrivateLinkResource {
  /** Fully qualified identifier of the resource. */
  id?: string;
  /** Name of the resource. */
  name?: string;
  /** Type of the resource. */
  type?: string;
  groupId?: string;
  displayName?: string;
  requiredMembers?: string[];
  requiredZoneNames?: string[];
}

/** Result of the List private link resources operation. */
export interface PrivateLinkResourcesListResult {
  /** A collection of private link resources */
  value?: PrivateLinkResource[];
  /** A link for the next page of private link resources. */
  nextLink?: string;
}

/** Properties of the System Topic update. */
export interface SystemTopicUpdateParameters {
  /** Tags of the system topic. */
  tags?: { [propertyName: string]: string };
  /** Resource identity information. */
  identity?: IdentityInfo;
}

/** Result of the List System topics operation. */
export interface SystemTopicsListResult {
  /** A collection of system Topics. */
  value?: SystemTopic[];
  /** A link for the next page of topics. */
  nextLink?: string;
}

/** Result of the List Event Types operation */
export interface EventTypesListResult {
  /** A collection of event types */
  value?: EventType[];
}

/** Result of the List Topic Space operation. */
export interface TopicSpacesListResult {
  /** A collection of Topic Space. */
  value?: TopicSpace[];
  /** A link for the next page of Topic Space. */
  nextLink?: string;
}

/** Result of the List Topic Types operation */
export interface TopicTypesListResult {
  /** A collection of topic types */
  value?: TopicTypeInfo[];
}

export interface TopicTypeAdditionalEnforcedPermission {
  permissionName?: string;
  isDataAction?: boolean;
}

/** Information about the partner. */
export interface PartnerDetails {
  /** This is short description about the partner. The length of this description should not exceed 256 characters. */
  description?: string;
  /** Long description for the partner's scenarios and integration.Length of this description should not exceed 2048 characters. */
  longDescription?: string;
  /**
   * URI of the partner website that can be used by Azure customers to setup Event Grid
   * integration on an event source.
   */
  setupUri?: string;
}

/** Result of the List verified partners operation */
export interface VerifiedPartnersListResult {
  /** A collection of verified partners. */
  value?: VerifiedPartner[];
  /** A link for the next page of verified partners if any. */
  nextLink?: string;
}

/** Namespace custom domain ownership validation result. */
export interface CustomDomainOwnershipValidationResult {
  /** List of custom domain configurations for the namespace under topics configuration. */
  customDomainsForTopicsConfiguration?: CustomDomainConfiguration[];
  /** List of custom domain configurations for the namespace under topic spaces configuration. */
  customDomainsForTopicSpacesConfiguration?: CustomDomainConfiguration[];
}

/** Full endpoint URL of an event subscription */
export interface SubscriptionFullUrl {
  /** The URL that represents the endpoint of the destination of an event subscription. */
  endpointUrl?: string;
}

/** This is used to express the source of an input schema mapping for a single target field in the Event Grid Event schema. This is currently used in the mappings for the 'id', 'topic' and 'eventtime' properties. This represents a field in the input event schema. */
export interface JsonField {
  /** Name of a field in the input event schema that's to be used as the source of a mapping. */
  sourceField?: string;
}

/**
 * This is used to express the source of an input schema mapping for a single target field
 * in the Event Grid Event schema. This is currently used in the mappings for the 'subject',
 * 'eventtype' and 'dataversion' properties. This represents a field in the input event schema
 * along with a default value to be used, and at least one of these two properties should be provided.
 */
export interface JsonFieldWithDefault {
  /** Name of a field in the input event schema that's to be used as the source of a mapping. */
  sourceField?: string;
  /** The default value to be used for mapping when a SourceField is not provided or if there's no property with the specified name in the published JSON event payload. */
  defaultValue?: string;
}

/** The CA Certificate resource. */
export interface CaCertificate extends Resource {
  /**
   * The system metadata relating to the Event Grid resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly systemData?: SystemData;
  /** Description for the CA Certificate resource. */
  description?: string;
  /** Base64 encoded PEM (Privacy Enhanced Mail) format certificate data. */
  encodedCertificate?: string;
  /**
   * Certificate issue time in UTC. This is a read-only field.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly issueTimeInUtc?: Date;
  /**
   * Certificate expiry time in UTC. This is a read-only field.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly expiryTimeInUtc?: Date;
  /**
   * Provisioning state of the CA Certificate resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly provisioningState?: CaCertificateProvisioningState;
}

/** Channel info. */
export interface Channel extends Resource {
  /**
   * The system metadata relating to the Event Grid resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly systemData?: SystemData;
  /** The type of the event channel which represents the direction flow of events. */
  channelType?: ChannelType;
  /** This property should be populated when channelType is PartnerTopic and represents information about the partner topic resource corresponding to the channel. */
  partnerTopicInfo?: PartnerTopicInfo;
  /** Context or helpful message that can be used during the approval process by the subscriber. */
  messageForActivation?: string;
  /** Provisioning state of the channel. */
  provisioningState?: ChannelProvisioningState;
  /** The readiness state of the corresponding partner topic. */
  readinessState?: ReadinessState;
  /**
   * Expiration time of the channel. If this timer expires while the corresponding partner topic is never activated,
   * the channel and corresponding partner topic are deleted.
   */
  expirationTimeIfNotActivatedUtc?: Date;
}

/** The Client group resource. */
export interface ClientGroup extends Resource {
  /**
   * The system metadata relating to the Event Grid resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly systemData?: SystemData;
  /** Description for the Client Group resource. */
  description?: string;
  /**
   * The grouping query for the clients.
   * Example : attributes.keyName IN ['a', 'b', 'c'].
   */
  query?: string;
  /**
   * Provisioning state of the ClientGroup resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly provisioningState?: ClientGroupProvisioningState;
}

/** The Client resource. */
export interface Client extends Resource {
  /**
   * The system metadata relating to the Event Grid resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly systemData?: SystemData;
  /** Description for the Client resource. */
  description?: string;
  /** The name presented by the client for authentication. The default value is the name of the resource. */
  authenticationName?: string;
  /** The client certificate authentication information. */
  clientCertificateAuthentication?: ClientCertificateAuthentication;
  /** Indicates if the client is enabled or not. Default value is Enabled. */
  state?: ClientState;
  /**
   * Attributes for the client. Supported values are int, bool, string, string[].
   * Example:
   * "attributes": { "room": "345", "floor": 12, "deviceTypes": ["Fan", "Light"] }
   */
  attributes?: { [propertyName: string]: any };
  /**
   * Provisioning state of the Client resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly provisioningState?: ClientProvisioningState;
}

export interface PrivateEndpointConnection extends Resource {
  /** The Private Endpoint resource for this Connection. */
  privateEndpoint?: PrivateEndpoint;
  /** GroupIds from the private link service resource. */
  groupIds?: string[];
  /** Details about the state of the connection. */
  privateLinkServiceConnectionState?: ConnectionState;
  /** Provisioning state of the Private Endpoint Connection. */
  provisioningState?: ResourceProvisioningState;
}

/** Definition of a Tracked Resource. */
export interface TrackedResource extends Resource {
  /** Location of the resource. */
  location: string;
  /** Tags of the resource. */
  tags?: { [propertyName: string]: string };
}

/** Domain Topic. */
export interface DomainTopic extends Resource {
  /**
   * The system metadata relating to the Event Grid resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly systemData?: SystemData;
  /**
   * Provisioning state of the domain topic.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly provisioningState?: DomainTopicProvisioningState;
}

/** Event Subscription. */
export interface Subscription extends Resource {
  /**
   * The system metadata relating to the Event Grid resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly systemData?: SystemData;
  /**
   * Provisioning state of the event subscription.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly provisioningState?: SubscriptionProvisioningState;
  /** Information about the delivery configuration of the event subscription. */
  deliveryConfiguration?: DeliveryConfiguration;
  /** The event delivery schema for the event subscription. */
  eventDeliverySchema?: DeliverySchema;
  /** Information about the filter for the event subscription. */
  filtersConfiguration?: FiltersConfiguration;
  /** Expiration time of the event subscription. */
  expirationTimeUtc?: Date;
}

/** Event Subscription. */
export interface EventSubscription extends Resource {
  /**
   * The system metadata relating to the Event Grid resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly systemData?: SystemData;
  /**
   * Name of the topic of the event subscription.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly topic?: string;
  /**
   * Provisioning state of the event subscription.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly provisioningState?: EventSubscriptionProvisioningState;
  /**
   * Information about the destination where events have to be delivered for the event subscription.
   * Uses Azure Event Grid's identity to acquire the authentication tokens being used during delivery / dead-lettering.
   */
  destination?: EventSubscriptionDestinationUnion;
  /**
   * Information about the destination where events have to be delivered for the event subscription.
   * Uses the managed identity setup on the parent resource (namely, topic or domain) to acquire the authentication tokens being used during delivery / dead-lettering.
   */
  deliveryWithResourceIdentity?: DeliveryWithResourceIdentity;
  /** Information about the filter for the event subscription. */
  filter?: EventSubscriptionFilter;
  /** List of user defined labels. */
  labels?: string[];
  /** Expiration time of the event subscription. */
  expirationTimeUtc?: Date;
  /** The event delivery schema for the event subscription. */
  eventDeliverySchema?: EventDeliverySchema;
  /** The retry policy for events. This can be used to configure maximum number of delivery attempts and time to live for events. */
  retryPolicy?: RetryPolicy;
  /**
   * The dead letter destination of the event subscription. Any event that cannot be delivered to its' destination is sent to the dead letter destination.
   * Uses Azure Event Grid's identity to acquire the authentication tokens being used during delivery / dead-lettering.
   */
  deadLetterDestination?: DeadLetterDestinationUnion;
  /**
   * The dead letter destination of the event subscription. Any event that cannot be delivered to its' destination is sent to the dead letter destination.
   * Uses the managed identity setup on the parent resource (namely, topic or domain) to acquire the authentication tokens being used during delivery / dead-lettering.
   */
  deadLetterWithResourceIdentity?: DeadLetterWithResourceIdentity;
}

/** Namespace topic details. */
export interface NamespaceTopic extends Resource {
  /**
   * The system metadata relating to the Event Grid resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly systemData?: SystemData;
  /**
   * Provisioning state of the namespace topic.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly provisioningState?: NamespaceTopicProvisioningState;
  /** Publisher type of the namespace topic. */
  publisherType?: PublisherType;
  /** This determines the format that is expected for incoming events published to the topic. */
  inputSchema?: EventInputSchema;
  /**
   * Event retention for the namespace topic expressed in days. The property default value is 1 day.
   * Min event retention duration value is 1 day and max event retention duration value is 1 day.
   */
  eventRetentionInDays?: number;
}

/** Partner configuration information */
export interface PartnerConfiguration extends Resource {
  /**
   * The system metadata relating to the Event Grid resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly systemData?: SystemData;
  /** Location of the resource. */
  location?: string;
  /** Tags of the resource. */
  tags?: { [propertyName: string]: string };
  /** The details of authorized partners. */
  partnerAuthorization?: PartnerAuthorization;
  /** Provisioning state of the partner configuration. */
  provisioningState?: PartnerConfigurationProvisioningState;
}

/** The Permission binding resource. */
export interface PermissionBinding extends Resource {
  /**
   * The system metadata relating to the Event Grid resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly systemData?: SystemData;
  /** Description for the Permission Binding resource. */
  description?: string;
  /**
   * The name of the Topic Space resource that the permission is bound to.
   * The Topic space needs to be a resource under the same namespace the permission binding is a part of.
   */
  topicSpaceName?: string;
  /** The allowed permission. */
  permission?: PermissionType;
  /**
   * The name of the client group resource that the permission is bound to.
   * The client group needs to be a resource under the same namespace the permission binding is a part of.
   */
  clientGroupName?: string;
  /**
   * Provisioning state of the PermissionBinding resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly provisioningState?: PermissionBindingProvisioningState;
}

/** Event Type for a subject under a topic */
export interface EventType extends Resource {
  /** Display name of the event type. */
  displayName?: string;
  /** Description of the event type. */
  description?: string;
  /** URL of the schema for this event type. */
  schemaUrl?: string;
  /** IsInDefaultSet flag of the event type. */
  isInDefaultSet?: boolean;
}

/** Event grid Extension Topic. This is used for getting Event Grid related metrics for Azure resources. */
export interface ExtensionTopic extends Resource {
  /**
   * The system metadata relating to the Event Grid resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly systemData?: SystemData;
  /** Description of the extension topic. */
  description?: string;
  /** System topic resource id which is mapped to the source. */
  systemTopic?: string;
}

/** The Topic space resource. */
export interface TopicSpace extends Resource {
  /**
   * The system metadata relating to the Event Grid resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly systemData?: SystemData;
  /** Description for the Topic Space resource. */
  description?: string;
  /**
   * The topic filters in the topic space.
   * Example: "topicTemplates": [
   *               "devices/foo/bar",
   *               "devices/topic1/+",
   *               "devices/${principal.name}/${principal.attributes.keyName}" ].
   */
  topicTemplates?: string[];
  /**
   * Provisioning state of the TopicSpace resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly provisioningState?: TopicSpaceProvisioningState;
}

/** Properties of a topic type info. */
export interface TopicTypeInfo extends Resource {
  /** Namespace of the provider of the topic type. */
  provider?: string;
  /** Display Name for the topic type. */
  displayName?: string;
  /** Description of the topic type. */
  description?: string;
  /** Region type of the resource. */
  resourceRegionType?: ResourceRegionType;
  /** Provisioning state of the topic type. */
  provisioningState?: TopicTypeProvisioningState;
  /** List of locations supported by this topic type. */
  supportedLocations?: string[];
  /** Source resource format. */
  sourceResourceFormat?: string;
  /** Supported source scopes. */
  supportedScopesForSource?: TopicTypeSourceScope[];
  /** Flag to indicate that a topic type can support both regional or global system topics. */
  areRegionalAndGlobalSourcesSupported?: boolean;
  /** Permissions which are enforced for creating and updating system topics of this this topic type. */
  additionalEnforcedPermissions?: TopicTypeAdditionalEnforcedPermission[];
}

/** Verified partner information */
export interface VerifiedPartner extends Resource {
  /**
   * The system metadata relating to the Event Grid resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly systemData?: SystemData;
  /** ImmutableId of the corresponding partner registration. */
  partnerRegistrationImmutableId?: string;
  /** Official name of the Partner. */
  organizationName?: string;
  /** Display name of the verified partner. */
  partnerDisplayName?: string;
  /** Details of the partner topic scenario. */
  partnerTopicDetails?: PartnerDetails;
  /** Provisioning state of the verified partner. */
  provisioningState?: VerifiedPartnerProvisioningState;
}

/** This enables publishing to Event Grid using a custom input schema. This can be used to map properties from a custom input JSON schema to the Event Grid event schema. */
export interface JsonInputSchemaMapping extends InputSchemaMapping {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  inputSchemaMappingType: "Json";
  /** The mapping information for the Id property of the Event Grid Event. */
  id?: JsonField;
  /** The mapping information for the Topic property of the Event Grid Event. */
  topic?: JsonField;
  /** The mapping information for the EventTime property of the Event Grid Event. */
  eventTime?: JsonField;
  /** The mapping information for the EventType property of the Event Grid Event. */
  eventType?: JsonFieldWithDefault;
  /** The mapping information for the Subject property of the Event Grid Event. */
  subject?: JsonFieldWithDefault;
  /** The mapping information for the DataVersion property of the Event Grid Event. */
  dataVersion?: JsonFieldWithDefault;
}

/** Static delivery attribute mapping details. */
export interface StaticDeliveryAttributeMapping
  extends DeliveryAttributeMapping {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  type: "Static";
  /** Value of the delivery attribute. */
  value?: string;
  /** Boolean flag to tell if the attribute contains sensitive information . */
  isSecret?: boolean;
}

/** Dynamic delivery attribute mapping details. */
export interface DynamicDeliveryAttributeMapping
  extends DeliveryAttributeMapping {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  type: "Dynamic";
  /** JSON path in the event which contains attribute value. */
  sourceField?: string;
}

/** Information about the storage blob based dead letter destination. */
export interface StorageBlobDeadLetterDestination
  extends DeadLetterDestination {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  endpointType: "StorageBlob";
  /** The Azure Resource ID of the storage account that is the destination of the deadletter events */
  resourceId?: string;
  /** The name of the Storage blob container that is the destination of the deadletter events */
  blobContainerName?: string;
}

/** Information about the webhook destination for an event subscription. */
export interface WebHookEventSubscriptionDestination
  extends EventSubscriptionDestination {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  endpointType: "WebHook";
  /** The URL that represents the endpoint of the destination of an event subscription. */
  endpointUrl?: string;
  /**
   * The base URL that represents the endpoint of the destination of an event subscription.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly endpointBaseUrl?: string;
  /** Maximum number of events per batch. */
  maxEventsPerBatch?: number;
  /** Preferred batch size in Kilobytes. */
  preferredBatchSizeInKilobytes?: number;
  /** The Azure Active Directory Tenant ID to get the access token that will be included as the bearer token in delivery requests. */
  azureActiveDirectoryTenantId?: string;
  /** The Azure Active Directory Application ID or URI to get the access token that will be included as the bearer token in delivery requests. */
  azureActiveDirectoryApplicationIdOrUri?: string;
  /** Delivery attribute details. */
  deliveryAttributeMappings?: DeliveryAttributeMappingUnion[];
  /** Minimum TLS version that should be supported by webhook endpoint */
  minimumTlsVersionAllowed?: TlsVersion;
}

/** Information about the event hub destination for an event subscription. */
export interface EventHubEventSubscriptionDestination
  extends EventSubscriptionDestination {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  endpointType: "EventHub";
  /** The Azure Resource Id that represents the endpoint of an Event Hub destination of an event subscription. */
  resourceId?: string;
  /** Delivery attribute details. */
  deliveryAttributeMappings?: DeliveryAttributeMappingUnion[];
}

/** Information about the storage queue destination for an event subscription. */
export interface StorageQueueEventSubscriptionDestination
  extends EventSubscriptionDestination {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  endpointType: "StorageQueue";
  /** The Azure Resource ID of the storage account that contains the queue that is the destination of an event subscription. */
  resourceId?: string;
  /** The name of the Storage queue under a storage account that is the destination of an event subscription. */
  queueName?: string;
  /** Storage queue message time to live in seconds. This value cannot be zero or negative with the exception of using -1 to indicate that the Time To Live of the message is Infinite. */
  queueMessageTimeToLiveInSeconds?: number;
}

/** Information about the HybridConnection destination for an event subscription. */
export interface HybridConnectionEventSubscriptionDestination
  extends EventSubscriptionDestination {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  endpointType: "HybridConnection";
  /** The Azure Resource ID of an hybrid connection that is the destination of an event subscription. */
  resourceId?: string;
  /** Delivery attribute details. */
  deliveryAttributeMappings?: DeliveryAttributeMappingUnion[];
}

/** Information about the service bus destination for an event subscription. */
export interface ServiceBusQueueEventSubscriptionDestination
  extends EventSubscriptionDestination {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  endpointType: "ServiceBusQueue";
  /** The Azure Resource Id that represents the endpoint of the Service Bus destination of an event subscription. */
  resourceId?: string;
  /** Delivery attribute details. */
  deliveryAttributeMappings?: DeliveryAttributeMappingUnion[];
}

/** Information about the service bus topic destination for an event subscription. */
export interface ServiceBusTopicEventSubscriptionDestination
  extends EventSubscriptionDestination {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  endpointType: "ServiceBusTopic";
  /** The Azure Resource Id that represents the endpoint of the Service Bus Topic destination of an event subscription. */
  resourceId?: string;
  /** Delivery attribute details. */
  deliveryAttributeMappings?: DeliveryAttributeMappingUnion[];
}

/** Information about the azure function destination for an event subscription. */
export interface AzureFunctionEventSubscriptionDestination
  extends EventSubscriptionDestination {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  endpointType: "AzureFunction";
  /** The Azure Resource Id that represents the endpoint of the Azure Function destination of an event subscription. */
  resourceId?: string;
  /** Maximum number of events per batch. */
  maxEventsPerBatch?: number;
  /** Preferred batch size in Kilobytes. */
  preferredBatchSizeInKilobytes?: number;
  /** Delivery attribute details. */
  deliveryAttributeMappings?: DeliveryAttributeMappingUnion[];
}

/** Information about the Monitor Alert destination for an event subscription. */
export interface MonitorAlertEventSubscriptionDestination
  extends EventSubscriptionDestination {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  endpointType: "MonitorAlert";
  /**
   * The severity that will be attached to every Alert fired through this event subscription.
   * This field must be provided.
   */
  severity?: MonitorAlertSeverity;
  /** The description that will be attached to every Alert fired through this event subscription. */
  description?: string;
  /**
   * The list of ARM Ids of Action Groups that will be triggered on every Alert fired through this event subscription.
   * Each resource ARM Id should follow this pattern: /subscriptions/{AzureSubscriptionId}/resourceGroups/{ResourceGroupName}/providers/Microsoft.Insights/actionGroups/{ActionGroupName}.
   */
  actionGroups?: string[];
}

/** Information about the Namespace Topic destination for an event subscription. */
export interface NamespaceTopicEventSubscriptionDestination
  extends EventSubscriptionDestination {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  endpointType: "NamespaceTopic";
  /**
   * The Azure resource Id that represents the endpoint of the Event Grid Namespace Topic destination of an event subscription.
   * This field is required and the Namespace Topic resource listed must already exist.
   * The resource ARM Id should follow this pattern: /subscriptions/{AzureSubscriptionId}/resourceGroups/{ResourceGroupName}/providers/Microsoft.EventGrid/namespaces/{NamespaceName}/topics/{TopicName}.
   */
  resourceId?: string;
}

/** NumberIn Filter. */
export interface NumberInFilter extends Filter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "NumberIn";
  /** The set of filter values. */
  values?: number[];
}

/** NumberNotIn Filter. */
export interface NumberNotInFilter extends Filter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "NumberNotIn";
  /** The set of filter values. */
  values?: number[];
}

/** NumberLessThan Filter. */
export interface NumberLessThanFilter extends Filter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "NumberLessThan";
  /** The filter value. */
  value?: number;
}

/** NumberGreaterThan Filter. */
export interface NumberGreaterThanFilter extends Filter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "NumberGreaterThan";
  /** The filter value. */
  value?: number;
}

/** NumberLessThanOrEquals Filter. */
export interface NumberLessThanOrEqualsFilter extends Filter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "NumberLessThanOrEquals";
  /** The filter value. */
  value?: number;
}

/** NumberGreaterThanOrEquals Filter. */
export interface NumberGreaterThanOrEqualsFilter extends Filter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "NumberGreaterThanOrEquals";
  /** The filter value. */
  value?: number;
}

/** BoolEquals Filter. */
export interface BoolEqualsFilter extends Filter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "BoolEquals";
  /** The boolean filter value. */
  value?: boolean;
}

/** StringIn Filter. */
export interface StringInFilter extends Filter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "StringIn";
  /** The set of filter values. */
  values?: string[];
}

/** StringNotIn Filter. */
export interface StringNotInFilter extends Filter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "StringNotIn";
  /** The set of filter values. */
  values?: string[];
}

/** StringBeginsWith Filter. */
export interface StringBeginsWithFilter extends Filter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "StringBeginsWith";
  /** The set of filter values. */
  values?: string[];
}

/** StringEndsWith Filter. */
export interface StringEndsWithFilter extends Filter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "StringEndsWith";
  /** The set of filter values. */
  values?: string[];
}

/** StringContains Filter. */
export interface StringContainsFilter extends Filter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "StringContains";
  /** The set of filter values. */
  values?: string[];
}

/** NumberInRange Filter. */
export interface NumberInRangeFilter extends Filter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "NumberInRange";
  /** The set of filter values. */
  values?: number[][];
}

/** NumberNotInRange Filter. */
export interface NumberNotInRangeFilter extends Filter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "NumberNotInRange";
  /** The set of filter values. */
  values?: number[][];
}

/** StringNotBeginsWith Filter. */
export interface StringNotBeginsWithFilter extends Filter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "StringNotBeginsWith";
  /** The set of filter values. */
  values?: string[];
}

/** StringNotEndsWith Filter. */
export interface StringNotEndsWithFilter extends Filter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "StringNotEndsWith";
  /** The set of filter values. */
  values?: string[];
}

/** StringNotContains Filter. */
export interface StringNotContainsFilter extends Filter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "StringNotContains";
  /** The set of filter values. */
  values?: string[];
}

/** IsNullOrUndefined Filter. */
export interface IsNullOrUndefinedFilter extends Filter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "IsNullOrUndefined";
}

/** IsNotNull Filter. */
export interface IsNotNullFilter extends Filter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "IsNotNull";
}

/** NumberIn Advanced Filter. */
export interface NumberInAdvancedFilter extends AdvancedFilter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "NumberIn";
  /** The set of filter values. */
  values?: number[];
}

/** NumberNotIn Advanced Filter. */
export interface NumberNotInAdvancedFilter extends AdvancedFilter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "NumberNotIn";
  /** The set of filter values. */
  values?: number[];
}

/** NumberLessThan Advanced Filter. */
export interface NumberLessThanAdvancedFilter extends AdvancedFilter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "NumberLessThan";
  /** The filter value. */
  value?: number;
}

/** NumberGreaterThan Advanced Filter. */
export interface NumberGreaterThanAdvancedFilter extends AdvancedFilter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "NumberGreaterThan";
  /** The filter value. */
  value?: number;
}

/** NumberLessThanOrEquals Advanced Filter. */
export interface NumberLessThanOrEqualsAdvancedFilter extends AdvancedFilter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "NumberLessThanOrEquals";
  /** The filter value. */
  value?: number;
}

/** NumberGreaterThanOrEquals Advanced Filter. */
export interface NumberGreaterThanOrEqualsAdvancedFilter
  extends AdvancedFilter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "NumberGreaterThanOrEquals";
  /** The filter value. */
  value?: number;
}

/** BoolEquals Advanced Filter. */
export interface BoolEqualsAdvancedFilter extends AdvancedFilter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "BoolEquals";
  /** The boolean filter value. */
  value?: boolean;
}

/** StringIn Advanced Filter. */
export interface StringInAdvancedFilter extends AdvancedFilter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "StringIn";
  /** The set of filter values. */
  values?: string[];
}

/** StringNotIn Advanced Filter. */
export interface StringNotInAdvancedFilter extends AdvancedFilter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "StringNotIn";
  /** The set of filter values. */
  values?: string[];
}

/** StringBeginsWith Advanced Filter. */
export interface StringBeginsWithAdvancedFilter extends AdvancedFilter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "StringBeginsWith";
  /** The set of filter values. */
  values?: string[];
}

/** StringEndsWith Advanced Filter. */
export interface StringEndsWithAdvancedFilter extends AdvancedFilter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "StringEndsWith";
  /** The set of filter values. */
  values?: string[];
}

/** StringContains Advanced Filter. */
export interface StringContainsAdvancedFilter extends AdvancedFilter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "StringContains";
  /** The set of filter values. */
  values?: string[];
}

/** NumberInRange Advanced Filter. */
export interface NumberInRangeAdvancedFilter extends AdvancedFilter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "NumberInRange";
  /** The set of filter values. */
  values?: number[][];
}

/** NumberNotInRange Advanced Filter. */
export interface NumberNotInRangeAdvancedFilter extends AdvancedFilter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "NumberNotInRange";
  /** The set of filter values. */
  values?: number[][];
}

/** StringNotBeginsWith Advanced Filter. */
export interface StringNotBeginsWithAdvancedFilter extends AdvancedFilter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "StringNotBeginsWith";
  /** The set of filter values. */
  values?: string[];
}

/** StringNotEndsWith Advanced Filter. */
export interface StringNotEndsWithAdvancedFilter extends AdvancedFilter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "StringNotEndsWith";
  /** The set of filter values. */
  values?: string[];
}

/** StringNotContains Advanced Filter. */
export interface StringNotContainsAdvancedFilter extends AdvancedFilter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "StringNotContains";
  /** The set of filter values. */
  values?: string[];
}

/** IsNullOrUndefined Advanced Filter. */
export interface IsNullOrUndefinedAdvancedFilter extends AdvancedFilter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "IsNullOrUndefined";
}

/** IsNotNull Advanced Filter. */
export interface IsNotNullAdvancedFilter extends AdvancedFilter {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  operatorType: "IsNotNull";
}

export interface StaticStringRoutingEnrichment extends StaticRoutingEnrichment {
  /** Polymorphic discriminator, which specifies the different types this object can be */
  valueType: "String";
  /** String type routing enrichment value. */
  value?: string;
}

/** EventGrid Domain. */
export interface Domain extends TrackedResource {
  /** Identity information for the Event Grid Domain resource. */
  identity?: IdentityInfo;
  /**
   * The system metadata relating to the Event Grid resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly systemData?: SystemData;
  /**
   * List of private endpoint connections.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly privateEndpointConnections?: PrivateEndpointConnection[];
  /**
   * Provisioning state of the Event Grid Domain Resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly provisioningState?: DomainProvisioningState;
  /** Minimum TLS version of the publisher allowed to publish to this domain */
  minimumTlsVersionAllowed?: TlsVersion;
  /**
   * Endpoint for the Event Grid Domain Resource which is used for publishing the events.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly endpoint?: string;
  /** This determines the format that Event Grid should expect for incoming events published to the Event Grid Domain Resource. */
  inputSchema?: InputSchema;
  /**
   * Event Type Information for the domain. This information is provided by the publisher and can be used by the
   * subscriber to view different types of events that are published.
   */
  eventTypeInfo?: EventTypeInfo;
  /** Information about the InputSchemaMapping which specified the info about mapping event payload. */
  inputSchemaMapping?: InputSchemaMappingUnion;
  /**
   * Metric resource id for the Event Grid Domain Resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly metricResourceId?: string;
  /**
   * This determines if traffic is allowed over public network. By default it is enabled.
   * You can further restrict to specific IPs by configuring <seealso cref="P:Microsoft.Azure.Events.ResourceProvider.Common.Contracts.DomainProperties.InboundIpRules" />
   */
  publicNetworkAccess?: PublicNetworkAccess;
  /** This can be used to restrict traffic from specific IPs instead of all IPs. Note: These are considered only if PublicNetworkAccess is enabled. */
  inboundIpRules?: InboundIpRule[];
  /** This boolean is used to enable or disable local auth. Default value is false. When the property is set to true, only AAD token will be used to authenticate if user is allowed to publish to the domain. */
  disableLocalAuth?: boolean;
  /**
   * This Boolean is used to specify the creation mechanism for 'all' the Event Grid Domain Topics associated with this Event Grid Domain resource.
   * In this context, creation of domain topic can be auto-managed (when true) or self-managed (when false). The default value for this property is true.
   * When this property is null or set to true, Event Grid is responsible of automatically creating the domain topic when the first event subscription is
   * created at the scope of the domain topic. If this property is set to false, then creating the first event subscription will require creating a domain topic
   * by the user. The self-management mode can be used if the user wants full control of when the domain topic is created, while auto-managed mode provides the
   * flexibility to perform less operations and manage fewer resources by the user. Also, note that in auto-managed creation mode, user is allowed to create the
   * domain topic on demand if needed.
   */
  autoCreateTopicWithFirstSubscription?: boolean;
  /**
   * This Boolean is used to specify the deletion mechanism for 'all' the Event Grid Domain Topics associated with this Event Grid Domain resource.
   * In this context, deletion of domain topic can be auto-managed (when true) or self-managed (when false). The default value for this property is true.
   * When this property is set to true, Event Grid is responsible of automatically deleting the domain topic when the last event subscription at the scope
   * of the domain topic is deleted. If this property is set to false, then the user needs to manually delete the domain topic when it is no longer needed
   * (e.g., when last event subscription is deleted and the resource needs to be cleaned up). The self-management mode can be used if the user wants full
   * control of when the domain topic needs to be deleted, while auto-managed mode provides the flexibility to perform less operations and manage fewer
   * resources by the user.
   */
  autoDeleteTopicWithLastSubscription?: boolean;
  /** Data Residency Boundary of the resource. */
  dataResidencyBoundary?: DataResidencyBoundary;
}

/** Namespace resource. */
export interface Namespace extends TrackedResource {
  /** Represents available Sku pricing tiers. */
  sku?: NamespaceSku;
  /** Identity information for the Namespace resource. */
  identity?: IdentityInfo;
  /**
   * The system metadata relating to the Event Grid resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly systemData?: SystemData;
  /** List of private endpoint connections. */
  privateEndpointConnections?: PrivateEndpointConnection[];
  /**
   * Provisioning state of the namespace resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly provisioningState?: NamespaceProvisioningState;
  /** Topics configuration information for the namespace resource */
  topicsConfiguration?: TopicsConfiguration;
  /** Topic spaces configuration information for the namespace resource */
  topicSpacesConfiguration?: TopicSpacesConfiguration;
  /**
   * This is an optional property and it allows the user to specify if the namespace resource supports zone-redundancy capability or not. If this
   * property is not specified explicitly by the user, its default value depends on the following conditions:
   *     a. For Availability Zones enabled regions - The default property value would be true.
   *     b. For non-Availability Zones enabled regions - The default property value would be false.
   * Once specified, this property cannot be updated.
   */
  isZoneRedundant?: boolean;
  /**
   * This determines if traffic is allowed over public network. By default it is enabled.
   * You can further restrict to specific IPs by configuring <seealso cref="P:Microsoft.Azure.Events.ResourceProvider.Common.Contracts.PubSub.NamespaceProperties.InboundIpRules" />
   */
  publicNetworkAccess?: PublicNetworkAccess;
  /** This can be used to restrict traffic from specific IPs instead of all IPs. Note: These are considered only if PublicNetworkAccess is enabled. */
  inboundIpRules?: InboundIpRule[];
  /** Minimum TLS version of the publisher allowed to publish to this namespace. Only TLS version 1.2 is supported. */
  minimumTlsVersionAllowed?: TlsVersion;
}

/** EventGrid Topic */
export interface Topic extends TrackedResource {
  /** Identity information for the resource. */
  identity?: IdentityInfo;
  /**
   * The system metadata relating to the Event Grid resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly systemData?: SystemData;
  /**
   * List of private endpoint connections.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly privateEndpointConnections?: PrivateEndpointConnection[];
  /**
   * Provisioning state of the topic.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly provisioningState?: TopicProvisioningState;
  /**
   * Endpoint for the topic.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly endpoint?: string;
  /**
   * Event Type Information for the user topic. This information is provided by the publisher and can be used by the
   * subscriber to view different types of events that are published.
   */
  eventTypeInfo?: EventTypeInfo;
  /** Minimum TLS version of the publisher allowed to publish to this topic */
  minimumTlsVersionAllowed?: TlsVersion;
  /** This determines the format that Event Grid should expect for incoming events published to the topic. */
  inputSchema?: InputSchema;
  /** This enables publishing using custom event schemas. An InputSchemaMapping can be specified to map various properties of a source schema to various required properties of the EventGridEvent schema. */
  inputSchemaMapping?: InputSchemaMappingUnion;
  /**
   * Metric resource id for the topic.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly metricResourceId?: string;
  /**
   * This determines if traffic is allowed over public network. By default it is enabled.
   * You can further restrict to specific IPs by configuring <seealso cref="P:Microsoft.Azure.Events.ResourceProvider.Common.Contracts.TopicProperties.InboundIpRules" />
   */
  publicNetworkAccess?: PublicNetworkAccess;
  /** This can be used to restrict traffic from specific IPs instead of all IPs. Note: These are considered only if PublicNetworkAccess is enabled. */
  inboundIpRules?: InboundIpRule[];
  /** This boolean is used to enable or disable local auth. Default value is false. When the property is set to true, only AAD token will be used to authenticate if user is allowed to publish to the topic. */
  disableLocalAuth?: boolean;
  /** Data Residency Boundary of the resource. */
  dataResidencyBoundary?: DataResidencyBoundary;
}

/** EventGrid Partner Namespace. */
export interface PartnerNamespace extends TrackedResource {
  /**
   * The system metadata relating to the Event Grid resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly systemData?: SystemData;
  /**
   * List of private endpoint connections.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly privateEndpointConnections?: PrivateEndpointConnection[];
  /**
   * Provisioning state of the partner namespace.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly provisioningState?: PartnerNamespaceProvisioningState;
  /**
   * The fully qualified ARM Id of the partner registration that should be associated with this partner namespace. This takes the following format:
   * /subscriptions/{subscriptionId}/resourceGroups/{resourceGroupName}/providers/Microsoft.EventGrid/partnerRegistrations/{partnerRegistrationName}.
   */
  partnerRegistrationFullyQualifiedId?: string;
  /** Minimum TLS version of the publisher allowed to publish to this partner namespace */
  minimumTlsVersionAllowed?: TlsVersion;
  /**
   * Endpoint for the partner namespace.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly endpoint?: string;
  /**
   * This determines if traffic is allowed over public network. By default it is enabled.
   * You can further restrict to specific IPs by configuring <seealso cref="P:Microsoft.Azure.Events.ResourceProvider.Common.Contracts.PartnerNamespaceProperties.InboundIpRules" />
   */
  publicNetworkAccess?: PublicNetworkAccess;
  /** This can be used to restrict traffic from specific IPs instead of all IPs. Note: These are considered only if PublicNetworkAccess is enabled. */
  inboundIpRules?: InboundIpRule[];
  /** This boolean is used to enable or disable local auth. Default value is false. When the property is set to true, only AAD token will be used to authenticate if user is allowed to publish to the partner namespace. */
  disableLocalAuth?: boolean;
  /**
   * This determines if events published to this partner namespace should use the source attribute in the event payload
   * or use the channel name in the header when matching to the partner topic. If none is specified, source attribute routing will be used to match the partner topic.
   */
  partnerTopicRoutingMode?: PartnerTopicRoutingMode;
}

/** Information about a partner registration. */
export interface PartnerRegistration extends TrackedResource {
  /**
   * The system metadata relating to the Event Grid resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly systemData?: SystemData;
  /**
   * Provisioning state of the partner registration.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly provisioningState?: PartnerRegistrationProvisioningState;
  /**
   * The immutableId of the corresponding partner registration.
   * Note: This property is marked for deprecation and is not supported in any future GA API version
   */
  partnerRegistrationImmutableId?: string;
}

/** Event Grid Partner Topic. */
export interface PartnerTopic extends TrackedResource {
  /**
   * The system metadata relating to the Event Grid resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly systemData?: SystemData;
  /** Identity information for the Partner Topic resource. */
  identity?: IdentityInfo;
  /** The immutableId of the corresponding partner registration. */
  partnerRegistrationImmutableId?: string;
  /** Source associated with this partner topic. This represents a unique partner resource. */
  source?: string;
  /** Event Type information from the corresponding event channel. */
  eventTypeInfo?: EventTypeInfo;
  /**
   * Expiration time of the partner topic. If this timer expires while the partner topic is still never activated,
   * the partner topic and corresponding event channel are deleted.
   */
  expirationTimeIfNotActivatedUtc?: Date;
  /**
   * Provisioning state of the partner topic.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly provisioningState?: PartnerTopicProvisioningState;
  /** Activation state of the partner topic. */
  activationState?: PartnerTopicActivationState;
  /**
   * Friendly description about the topic. This can be set by the publisher/partner to show custom description for the customer partner topic.
   * This will be helpful to remove any ambiguity of the origin of creation of the partner topic for the customer.
   */
  partnerTopicFriendlyDescription?: string;
  /** Context or helpful message that can be used during the approval process by the subscriber. */
  messageForActivation?: string;
}

/** EventGrid System Topic. */
export interface SystemTopic extends TrackedResource {
  /**
   * The system metadata relating to the Event Grid resource.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly systemData?: SystemData;
  /** Identity information for the resource. */
  identity?: IdentityInfo;
  /**
   * Provisioning state of the system topic.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly provisioningState?: ResourceProvisioningState;
  /** Source for the system topic. */
  source?: string;
  /** TopicType for the system topic. */
  topicType?: string;
  /**
   * Metric resource id for the system topic.
   * NOTE: This property will not be serialized. It can only be populated by the server.
   */
  readonly metricResourceId?: string;
}

/** Defines headers for CaCertificates_delete operation. */
export interface CaCertificatesDeleteHeaders {
  location?: string;
}

/** Defines headers for Channels_delete operation. */
export interface ChannelsDeleteHeaders {
  location?: string;
}

/** Defines headers for ClientGroups_delete operation. */
export interface ClientGroupsDeleteHeaders {
  location?: string;
}

/** Defines headers for Clients_delete operation. */
export interface ClientsDeleteHeaders {
  location?: string;
}

/** Defines headers for Domains_delete operation. */
export interface DomainsDeleteHeaders {
  location?: string;
}

/** Defines headers for DomainTopics_delete operation. */
export interface DomainTopicsDeleteHeaders {
  location?: string;
}

/** Defines headers for TopicEventSubscriptions_delete operation. */
export interface TopicEventSubscriptionsDeleteHeaders {
  location?: string;
}

/** Defines headers for DomainEventSubscriptions_delete operation. */
export interface DomainEventSubscriptionsDeleteHeaders {
  location?: string;
}

/** Defines headers for NamespaceTopicEventSubscriptions_delete operation. */
export interface NamespaceTopicEventSubscriptionsDeleteHeaders {
  location?: string;
}

/** Defines headers for NamespaceTopicEventSubscriptions_update operation. */
export interface NamespaceTopicEventSubscriptionsUpdateHeaders {
  location?: string;
}

/** Defines headers for EventSubscriptions_delete operation. */
export interface EventSubscriptionsDeleteHeaders {
  location?: string;
}

/** Defines headers for DomainTopicEventSubscriptions_delete operation. */
export interface DomainTopicEventSubscriptionsDeleteHeaders {
  location?: string;
}

/** Defines headers for SystemTopicEventSubscriptions_delete operation. */
export interface SystemTopicEventSubscriptionsDeleteHeaders {
  location?: string;
}

/** Defines headers for PartnerTopicEventSubscriptions_delete operation. */
export interface PartnerTopicEventSubscriptionsDeleteHeaders {
  location?: string;
}

/** Defines headers for Namespaces_delete operation. */
export interface NamespacesDeleteHeaders {
  location?: string;
}

/** Defines headers for Namespaces_update operation. */
export interface NamespacesUpdateHeaders {
  location?: string;
}

/** Defines headers for Namespaces_regenerateKey operation. */
export interface NamespacesRegenerateKeyHeaders {
  location?: string;
}

/** Defines headers for Namespaces_validateCustomDomainOwnership operation. */
export interface NamespacesValidateCustomDomainOwnershipHeaders {
  location?: string;
}

/** Defines headers for NamespaceTopics_delete operation. */
export interface NamespaceTopicsDeleteHeaders {
  location?: string;
}

/** Defines headers for NamespaceTopics_update operation. */
export interface NamespaceTopicsUpdateHeaders {
  location?: string;
}

/** Defines headers for NamespaceTopics_regenerateKey operation. */
export interface NamespaceTopicsRegenerateKeyHeaders {
  location?: string;
}

/** Defines headers for Topics_delete operation. */
export interface TopicsDeleteHeaders {
  location?: string;
}

/** Defines headers for Topics_regenerateKey operation. */
export interface TopicsRegenerateKeyHeaders {
  location?: string;
}

/** Defines headers for PartnerConfigurations_delete operation. */
export interface PartnerConfigurationsDeleteHeaders {
  location?: string;
}

/** Defines headers for PartnerNamespaces_delete operation. */
export interface PartnerNamespacesDeleteHeaders {
  location?: string;
}

/** Defines headers for PartnerRegistrations_delete operation. */
export interface PartnerRegistrationsDeleteHeaders {
  location?: string;
}

/** Defines headers for PartnerTopics_delete operation. */
export interface PartnerTopicsDeleteHeaders {
  location?: string;
}

/** Defines headers for PermissionBindings_delete operation. */
export interface PermissionBindingsDeleteHeaders {
  location?: string;
}

/** Defines headers for PrivateEndpointConnections_delete operation. */
export interface PrivateEndpointConnectionsDeleteHeaders {
  location?: string;
}

/** Defines headers for SystemTopics_delete operation. */
export interface SystemTopicsDeleteHeaders {
  location?: string;
}

/** Defines headers for TopicSpaces_delete operation. */
export interface TopicSpacesDeleteHeaders {
  location?: string;
}

/** Known values of {@link CaCertificateProvisioningState} that the service accepts. */
export enum KnownCaCertificateProvisioningState {
  /** Creating */
  Creating = "Creating",
  /** Updating */
  Updating = "Updating",
  /** Deleting */
  Deleting = "Deleting",
  /** Succeeded */
  Succeeded = "Succeeded",
  /** Canceled */
  Canceled = "Canceled",
  /** Failed */
  Failed = "Failed",
  /** Deleted */
  Deleted = "Deleted",
}

/**
 * Defines values for CaCertificateProvisioningState. \
 * {@link KnownCaCertificateProvisioningState} can be used interchangeably with CaCertificateProvisioningState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Creating** \
 * **Updating** \
 * **Deleting** \
 * **Succeeded** \
 * **Canceled** \
 * **Failed** \
 * **Deleted**
 */
export type CaCertificateProvisioningState = string;

/** Known values of {@link CreatedByType} that the service accepts. */
export enum KnownCreatedByType {
  /** User */
  User = "User",
  /** Application */
  Application = "Application",
  /** ManagedIdentity */
  ManagedIdentity = "ManagedIdentity",
  /** Key */
  Key = "Key",
}

/**
 * Defines values for CreatedByType. \
 * {@link KnownCreatedByType} can be used interchangeably with CreatedByType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **User** \
 * **Application** \
 * **ManagedIdentity** \
 * **Key**
 */
export type CreatedByType = string;

/** Known values of {@link ChannelType} that the service accepts. */
export enum KnownChannelType {
  /** PartnerTopic */
  PartnerTopic = "PartnerTopic",
}

/**
 * Defines values for ChannelType. \
 * {@link KnownChannelType} can be used interchangeably with ChannelType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **PartnerTopic**
 */
export type ChannelType = string;

/** Known values of {@link EventDefinitionKind} that the service accepts. */
export enum KnownEventDefinitionKind {
  /** Inline */
  Inline = "Inline",
}

/**
 * Defines values for EventDefinitionKind. \
 * {@link KnownEventDefinitionKind} can be used interchangeably with EventDefinitionKind,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Inline**
 */
export type EventDefinitionKind = string;

/** Known values of {@link ChannelProvisioningState} that the service accepts. */
export enum KnownChannelProvisioningState {
  /** Creating */
  Creating = "Creating",
  /** Updating */
  Updating = "Updating",
  /** Deleting */
  Deleting = "Deleting",
  /** Succeeded */
  Succeeded = "Succeeded",
  /** Canceled */
  Canceled = "Canceled",
  /** Failed */
  Failed = "Failed",
  /** IdleDueToMirroredPartnerTopicDeletion */
  IdleDueToMirroredPartnerTopicDeletion = "IdleDueToMirroredPartnerTopicDeletion",
}

/**
 * Defines values for ChannelProvisioningState. \
 * {@link KnownChannelProvisioningState} can be used interchangeably with ChannelProvisioningState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Creating** \
 * **Updating** \
 * **Deleting** \
 * **Succeeded** \
 * **Canceled** \
 * **Failed** \
 * **IdleDueToMirroredPartnerTopicDeletion**
 */
export type ChannelProvisioningState = string;

/** Known values of {@link ReadinessState} that the service accepts. */
export enum KnownReadinessState {
  /** NeverActivated */
  NeverActivated = "NeverActivated",
  /** Activated */
  Activated = "Activated",
}

/**
 * Defines values for ReadinessState. \
 * {@link KnownReadinessState} can be used interchangeably with ReadinessState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **NeverActivated** \
 * **Activated**
 */
export type ReadinessState = string;

/** Known values of {@link ClientGroupProvisioningState} that the service accepts. */
export enum KnownClientGroupProvisioningState {
  /** Creating */
  Creating = "Creating",
  /** Updating */
  Updating = "Updating",
  /** Deleting */
  Deleting = "Deleting",
  /** Succeeded */
  Succeeded = "Succeeded",
  /** Canceled */
  Canceled = "Canceled",
  /** Failed */
  Failed = "Failed",
  /** Deleted */
  Deleted = "Deleted",
}

/**
 * Defines values for ClientGroupProvisioningState. \
 * {@link KnownClientGroupProvisioningState} can be used interchangeably with ClientGroupProvisioningState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Creating** \
 * **Updating** \
 * **Deleting** \
 * **Succeeded** \
 * **Canceled** \
 * **Failed** \
 * **Deleted**
 */
export type ClientGroupProvisioningState = string;

/** Known values of {@link ClientCertificateValidationScheme} that the service accepts. */
export enum KnownClientCertificateValidationScheme {
  /** SubjectMatchesAuthenticationName */
  SubjectMatchesAuthenticationName = "SubjectMatchesAuthenticationName",
  /** DnsMatchesAuthenticationName */
  DnsMatchesAuthenticationName = "DnsMatchesAuthenticationName",
  /** UriMatchesAuthenticationName */
  UriMatchesAuthenticationName = "UriMatchesAuthenticationName",
  /** IpMatchesAuthenticationName */
  IpMatchesAuthenticationName = "IpMatchesAuthenticationName",
  /** EmailMatchesAuthenticationName */
  EmailMatchesAuthenticationName = "EmailMatchesAuthenticationName",
  /** ThumbprintMatch */
  ThumbprintMatch = "ThumbprintMatch",
}

/**
 * Defines values for ClientCertificateValidationScheme. \
 * {@link KnownClientCertificateValidationScheme} can be used interchangeably with ClientCertificateValidationScheme,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **SubjectMatchesAuthenticationName** \
 * **DnsMatchesAuthenticationName** \
 * **UriMatchesAuthenticationName** \
 * **IpMatchesAuthenticationName** \
 * **EmailMatchesAuthenticationName** \
 * **ThumbprintMatch**
 */
export type ClientCertificateValidationScheme = string;

/** Known values of {@link ClientState} that the service accepts. */
export enum KnownClientState {
  /** Enabled */
  Enabled = "Enabled",
  /** Disabled */
  Disabled = "Disabled",
}

/**
 * Defines values for ClientState. \
 * {@link KnownClientState} can be used interchangeably with ClientState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Enabled** \
 * **Disabled**
 */
export type ClientState = string;

/** Known values of {@link ClientProvisioningState} that the service accepts. */
export enum KnownClientProvisioningState {
  /** Creating */
  Creating = "Creating",
  /** Updating */
  Updating = "Updating",
  /** Deleting */
  Deleting = "Deleting",
  /** Succeeded */
  Succeeded = "Succeeded",
  /** Canceled */
  Canceled = "Canceled",
  /** Failed */
  Failed = "Failed",
  /** Deleted */
  Deleted = "Deleted",
}

/**
 * Defines values for ClientProvisioningState. \
 * {@link KnownClientProvisioningState} can be used interchangeably with ClientProvisioningState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Creating** \
 * **Updating** \
 * **Deleting** \
 * **Succeeded** \
 * **Canceled** \
 * **Failed** \
 * **Deleted**
 */
export type ClientProvisioningState = string;

/** Known values of {@link PersistedConnectionStatus} that the service accepts. */
export enum KnownPersistedConnectionStatus {
  /** Pending */
  Pending = "Pending",
  /** Approved */
  Approved = "Approved",
  /** Rejected */
  Rejected = "Rejected",
  /** Disconnected */
  Disconnected = "Disconnected",
}

/**
 * Defines values for PersistedConnectionStatus. \
 * {@link KnownPersistedConnectionStatus} can be used interchangeably with PersistedConnectionStatus,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Pending** \
 * **Approved** \
 * **Rejected** \
 * **Disconnected**
 */
export type PersistedConnectionStatus = string;

/** Known values of {@link ResourceProvisioningState} that the service accepts. */
export enum KnownResourceProvisioningState {
  /** Creating */
  Creating = "Creating",
  /** Updating */
  Updating = "Updating",
  /** Deleting */
  Deleting = "Deleting",
  /** Succeeded */
  Succeeded = "Succeeded",
  /** Canceled */
  Canceled = "Canceled",
  /** Failed */
  Failed = "Failed",
}

/**
 * Defines values for ResourceProvisioningState. \
 * {@link KnownResourceProvisioningState} can be used interchangeably with ResourceProvisioningState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Creating** \
 * **Updating** \
 * **Deleting** \
 * **Succeeded** \
 * **Canceled** \
 * **Failed**
 */
export type ResourceProvisioningState = string;

/** Known values of {@link DomainProvisioningState} that the service accepts. */
export enum KnownDomainProvisioningState {
  /** Creating */
  Creating = "Creating",
  /** Updating */
  Updating = "Updating",
  /** Deleting */
  Deleting = "Deleting",
  /** Succeeded */
  Succeeded = "Succeeded",
  /** Canceled */
  Canceled = "Canceled",
  /** Failed */
  Failed = "Failed",
}

/**
 * Defines values for DomainProvisioningState. \
 * {@link KnownDomainProvisioningState} can be used interchangeably with DomainProvisioningState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Creating** \
 * **Updating** \
 * **Deleting** \
 * **Succeeded** \
 * **Canceled** \
 * **Failed**
 */
export type DomainProvisioningState = string;

/** Known values of {@link TlsVersion} that the service accepts. */
export enum KnownTlsVersion {
  /** One0 */
  One0 = "1.0",
  /** One1 */
  One1 = "1.1",
  /** One2 */
  One2 = "1.2",
}

/**
 * Defines values for TlsVersion. \
 * {@link KnownTlsVersion} can be used interchangeably with TlsVersion,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **1.0** \
 * **1.1** \
 * **1.2**
 */
export type TlsVersion = string;

/** Known values of {@link InputSchema} that the service accepts. */
export enum KnownInputSchema {
  /** EventGridSchema */
  EventGridSchema = "EventGridSchema",
  /** CustomEventSchema */
  CustomEventSchema = "CustomEventSchema",
  /** CloudEventSchemaV10 */
  CloudEventSchemaV10 = "CloudEventSchemaV1_0",
}

/**
 * Defines values for InputSchema. \
 * {@link KnownInputSchema} can be used interchangeably with InputSchema,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **EventGridSchema** \
 * **CustomEventSchema** \
 * **CloudEventSchemaV1_0**
 */
export type InputSchema = string;

/** Known values of {@link InputSchemaMappingType} that the service accepts. */
export enum KnownInputSchemaMappingType {
  /** Json */
  Json = "Json",
}

/**
 * Defines values for InputSchemaMappingType. \
 * {@link KnownInputSchemaMappingType} can be used interchangeably with InputSchemaMappingType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Json**
 */
export type InputSchemaMappingType = string;

/** Known values of {@link PublicNetworkAccess} that the service accepts. */
export enum KnownPublicNetworkAccess {
  /** Enabled */
  Enabled = "Enabled",
  /** Disabled */
  Disabled = "Disabled",
}

/**
 * Defines values for PublicNetworkAccess. \
 * {@link KnownPublicNetworkAccess} can be used interchangeably with PublicNetworkAccess,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Enabled** \
 * **Disabled**
 */
export type PublicNetworkAccess = string;

/** Known values of {@link IpActionType} that the service accepts. */
export enum KnownIpActionType {
  /** Allow */
  Allow = "Allow",
}

/**
 * Defines values for IpActionType. \
 * {@link KnownIpActionType} can be used interchangeably with IpActionType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Allow**
 */
export type IpActionType = string;

/** Known values of {@link DataResidencyBoundary} that the service accepts. */
export enum KnownDataResidencyBoundary {
  /** WithinGeopair */
  WithinGeopair = "WithinGeopair",
  /** WithinRegion */
  WithinRegion = "WithinRegion",
}

/**
 * Defines values for DataResidencyBoundary. \
 * {@link KnownDataResidencyBoundary} can be used interchangeably with DataResidencyBoundary,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **WithinGeopair** \
 * **WithinRegion**
 */
export type DataResidencyBoundary = string;

/** Known values of {@link IdentityType} that the service accepts. */
export enum KnownIdentityType {
  /** None */
  None = "None",
  /** SystemAssigned */
  SystemAssigned = "SystemAssigned",
  /** UserAssigned */
  UserAssigned = "UserAssigned",
  /** SystemAssignedUserAssigned */
  SystemAssignedUserAssigned = "SystemAssigned, UserAssigned",
}

/**
 * Defines values for IdentityType. \
 * {@link KnownIdentityType} can be used interchangeably with IdentityType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **None** \
 * **SystemAssigned** \
 * **UserAssigned** \
 * **SystemAssigned, UserAssigned**
 */
export type IdentityType = string;

/** Known values of {@link DomainTopicProvisioningState} that the service accepts. */
export enum KnownDomainTopicProvisioningState {
  /** Creating */
  Creating = "Creating",
  /** Updating */
  Updating = "Updating",
  /** Deleting */
  Deleting = "Deleting",
  /** Succeeded */
  Succeeded = "Succeeded",
  /** Canceled */
  Canceled = "Canceled",
  /** Failed */
  Failed = "Failed",
}

/**
 * Defines values for DomainTopicProvisioningState. \
 * {@link KnownDomainTopicProvisioningState} can be used interchangeably with DomainTopicProvisioningState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Creating** \
 * **Updating** \
 * **Deleting** \
 * **Succeeded** \
 * **Canceled** \
 * **Failed**
 */
export type DomainTopicProvisioningState = string;

/** Known values of {@link DeliveryAttributeMappingType} that the service accepts. */
export enum KnownDeliveryAttributeMappingType {
  /** Static */
  Static = "Static",
  /** Dynamic */
  Dynamic = "Dynamic",
}

/**
 * Defines values for DeliveryAttributeMappingType. \
 * {@link KnownDeliveryAttributeMappingType} can be used interchangeably with DeliveryAttributeMappingType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Static** \
 * **Dynamic**
 */
export type DeliveryAttributeMappingType = string;

/** Known values of {@link SubscriptionProvisioningState} that the service accepts. */
export enum KnownSubscriptionProvisioningState {
  /** Creating */
  Creating = "Creating",
  /** Updating */
  Updating = "Updating",
  /** Deleting */
  Deleting = "Deleting",
  /** Succeeded */
  Succeeded = "Succeeded",
  /** Canceled */
  Canceled = "Canceled",
  /** Failed */
  Failed = "Failed",
  /** AwaitingManualAction */
  AwaitingManualAction = "AwaitingManualAction",
  /** Deleted */
  Deleted = "Deleted",
  /** DeleteFailed */
  DeleteFailed = "DeleteFailed",
  /** CreateFailed */
  CreateFailed = "CreateFailed",
  /** UpdatedFailed */
  UpdatedFailed = "UpdatedFailed",
}

/**
 * Defines values for SubscriptionProvisioningState. \
 * {@link KnownSubscriptionProvisioningState} can be used interchangeably with SubscriptionProvisioningState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Creating** \
 * **Updating** \
 * **Deleting** \
 * **Succeeded** \
 * **Canceled** \
 * **Failed** \
 * **AwaitingManualAction** \
 * **Deleted** \
 * **DeleteFailed** \
 * **CreateFailed** \
 * **UpdatedFailed**
 */
export type SubscriptionProvisioningState = string;

/** Known values of {@link DeliveryMode} that the service accepts. */
export enum KnownDeliveryMode {
  /** Queue */
  Queue = "Queue",
  /** Push */
  Push = "Push",
}

/**
 * Defines values for DeliveryMode. \
 * {@link KnownDeliveryMode} can be used interchangeably with DeliveryMode,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Queue** \
 * **Push**
 */
export type DeliveryMode = string;

/** Known values of {@link EventSubscriptionIdentityType} that the service accepts. */
export enum KnownEventSubscriptionIdentityType {
  /** SystemAssigned */
  SystemAssigned = "SystemAssigned",
  /** UserAssigned */
  UserAssigned = "UserAssigned",
}

/**
 * Defines values for EventSubscriptionIdentityType. \
 * {@link KnownEventSubscriptionIdentityType} can be used interchangeably with EventSubscriptionIdentityType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **SystemAssigned** \
 * **UserAssigned**
 */
export type EventSubscriptionIdentityType = string;

/** Known values of {@link DeadLetterEndPointType} that the service accepts. */
export enum KnownDeadLetterEndPointType {
  /** StorageBlob */
  StorageBlob = "StorageBlob",
}

/**
 * Defines values for DeadLetterEndPointType. \
 * {@link KnownDeadLetterEndPointType} can be used interchangeably with DeadLetterEndPointType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **StorageBlob**
 */
export type DeadLetterEndPointType = string;

/** Known values of {@link EndpointType} that the service accepts. */
export enum KnownEndpointType {
  /** WebHook */
  WebHook = "WebHook",
  /** EventHub */
  EventHub = "EventHub",
  /** StorageQueue */
  StorageQueue = "StorageQueue",
  /** HybridConnection */
  HybridConnection = "HybridConnection",
  /** ServiceBusQueue */
  ServiceBusQueue = "ServiceBusQueue",
  /** ServiceBusTopic */
  ServiceBusTopic = "ServiceBusTopic",
  /** AzureFunction */
  AzureFunction = "AzureFunction",
  /** MonitorAlert */
  MonitorAlert = "MonitorAlert",
  /** NamespaceTopic */
  NamespaceTopic = "NamespaceTopic",
}

/**
 * Defines values for EndpointType. \
 * {@link KnownEndpointType} can be used interchangeably with EndpointType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **WebHook** \
 * **EventHub** \
 * **StorageQueue** \
 * **HybridConnection** \
 * **ServiceBusQueue** \
 * **ServiceBusTopic** \
 * **AzureFunction** \
 * **MonitorAlert** \
 * **NamespaceTopic**
 */
export type EndpointType = string;

/** Known values of {@link DeliverySchema} that the service accepts. */
export enum KnownDeliverySchema {
  /** CloudEventSchemaV10 */
  CloudEventSchemaV10 = "CloudEventSchemaV1_0",
}

/**
 * Defines values for DeliverySchema. \
 * {@link KnownDeliverySchema} can be used interchangeably with DeliverySchema,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **CloudEventSchemaV1_0**
 */
export type DeliverySchema = string;

/** Known values of {@link FilterOperatorType} that the service accepts. */
export enum KnownFilterOperatorType {
  /** NumberIn */
  NumberIn = "NumberIn",
  /** NumberNotIn */
  NumberNotIn = "NumberNotIn",
  /** NumberLessThan */
  NumberLessThan = "NumberLessThan",
  /** NumberGreaterThan */
  NumberGreaterThan = "NumberGreaterThan",
  /** NumberLessThanOrEquals */
  NumberLessThanOrEquals = "NumberLessThanOrEquals",
  /** NumberGreaterThanOrEquals */
  NumberGreaterThanOrEquals = "NumberGreaterThanOrEquals",
  /** BoolEquals */
  BoolEquals = "BoolEquals",
  /** StringIn */
  StringIn = "StringIn",
  /** StringNotIn */
  StringNotIn = "StringNotIn",
  /** StringBeginsWith */
  StringBeginsWith = "StringBeginsWith",
  /** StringEndsWith */
  StringEndsWith = "StringEndsWith",
  /** StringContains */
  StringContains = "StringContains",
  /** NumberInRange */
  NumberInRange = "NumberInRange",
  /** NumberNotInRange */
  NumberNotInRange = "NumberNotInRange",
  /** StringNotBeginsWith */
  StringNotBeginsWith = "StringNotBeginsWith",
  /** StringNotEndsWith */
  StringNotEndsWith = "StringNotEndsWith",
  /** StringNotContains */
  StringNotContains = "StringNotContains",
  /** IsNullOrUndefined */
  IsNullOrUndefined = "IsNullOrUndefined",
  /** IsNotNull */
  IsNotNull = "IsNotNull",
}

/**
 * Defines values for FilterOperatorType. \
 * {@link KnownFilterOperatorType} can be used interchangeably with FilterOperatorType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **NumberIn** \
 * **NumberNotIn** \
 * **NumberLessThan** \
 * **NumberGreaterThan** \
 * **NumberLessThanOrEquals** \
 * **NumberGreaterThanOrEquals** \
 * **BoolEquals** \
 * **StringIn** \
 * **StringNotIn** \
 * **StringBeginsWith** \
 * **StringEndsWith** \
 * **StringContains** \
 * **NumberInRange** \
 * **NumberNotInRange** \
 * **StringNotBeginsWith** \
 * **StringNotEndsWith** \
 * **StringNotContains** \
 * **IsNullOrUndefined** \
 * **IsNotNull**
 */
export type FilterOperatorType = string;

/** Known values of {@link EventSubscriptionProvisioningState} that the service accepts. */
export enum KnownEventSubscriptionProvisioningState {
  /** Creating */
  Creating = "Creating",
  /** Updating */
  Updating = "Updating",
  /** Deleting */
  Deleting = "Deleting",
  /** Succeeded */
  Succeeded = "Succeeded",
  /** Canceled */
  Canceled = "Canceled",
  /** Failed */
  Failed = "Failed",
  /** AwaitingManualAction */
  AwaitingManualAction = "AwaitingManualAction",
}

/**
 * Defines values for EventSubscriptionProvisioningState. \
 * {@link KnownEventSubscriptionProvisioningState} can be used interchangeably with EventSubscriptionProvisioningState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Creating** \
 * **Updating** \
 * **Deleting** \
 * **Succeeded** \
 * **Canceled** \
 * **Failed** \
 * **AwaitingManualAction**
 */
export type EventSubscriptionProvisioningState = string;

/** Known values of {@link AdvancedFilterOperatorType} that the service accepts. */
export enum KnownAdvancedFilterOperatorType {
  /** NumberIn */
  NumberIn = "NumberIn",
  /** NumberNotIn */
  NumberNotIn = "NumberNotIn",
  /** NumberLessThan */
  NumberLessThan = "NumberLessThan",
  /** NumberGreaterThan */
  NumberGreaterThan = "NumberGreaterThan",
  /** NumberLessThanOrEquals */
  NumberLessThanOrEquals = "NumberLessThanOrEquals",
  /** NumberGreaterThanOrEquals */
  NumberGreaterThanOrEquals = "NumberGreaterThanOrEquals",
  /** BoolEquals */
  BoolEquals = "BoolEquals",
  /** StringIn */
  StringIn = "StringIn",
  /** StringNotIn */
  StringNotIn = "StringNotIn",
  /** StringBeginsWith */
  StringBeginsWith = "StringBeginsWith",
  /** StringEndsWith */
  StringEndsWith = "StringEndsWith",
  /** StringContains */
  StringContains = "StringContains",
  /** NumberInRange */
  NumberInRange = "NumberInRange",
  /** NumberNotInRange */
  NumberNotInRange = "NumberNotInRange",
  /** StringNotBeginsWith */
  StringNotBeginsWith = "StringNotBeginsWith",
  /** StringNotEndsWith */
  StringNotEndsWith = "StringNotEndsWith",
  /** StringNotContains */
  StringNotContains = "StringNotContains",
  /** IsNullOrUndefined */
  IsNullOrUndefined = "IsNullOrUndefined",
  /** IsNotNull */
  IsNotNull = "IsNotNull",
}

/**
 * Defines values for AdvancedFilterOperatorType. \
 * {@link KnownAdvancedFilterOperatorType} can be used interchangeably with AdvancedFilterOperatorType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **NumberIn** \
 * **NumberNotIn** \
 * **NumberLessThan** \
 * **NumberGreaterThan** \
 * **NumberLessThanOrEquals** \
 * **NumberGreaterThanOrEquals** \
 * **BoolEquals** \
 * **StringIn** \
 * **StringNotIn** \
 * **StringBeginsWith** \
 * **StringEndsWith** \
 * **StringContains** \
 * **NumberInRange** \
 * **NumberNotInRange** \
 * **StringNotBeginsWith** \
 * **StringNotEndsWith** \
 * **StringNotContains** \
 * **IsNullOrUndefined** \
 * **IsNotNull**
 */
export type AdvancedFilterOperatorType = string;

/** Known values of {@link EventDeliverySchema} that the service accepts. */
export enum KnownEventDeliverySchema {
  /** EventGridSchema */
  EventGridSchema = "EventGridSchema",
  /** CustomInputSchema */
  CustomInputSchema = "CustomInputSchema",
  /** CloudEventSchemaV10 */
  CloudEventSchemaV10 = "CloudEventSchemaV1_0",
}

/**
 * Defines values for EventDeliverySchema. \
 * {@link KnownEventDeliverySchema} can be used interchangeably with EventDeliverySchema,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **EventGridSchema** \
 * **CustomInputSchema** \
 * **CloudEventSchemaV1_0**
 */
export type EventDeliverySchema = string;

/** Known values of {@link NamespaceProvisioningState} that the service accepts. */
export enum KnownNamespaceProvisioningState {
  /** Creating */
  Creating = "Creating",
  /** Updating */
  Updating = "Updating",
  /** Deleting */
  Deleting = "Deleting",
  /** Succeeded */
  Succeeded = "Succeeded",
  /** Canceled */
  Canceled = "Canceled",
  /** Failed */
  Failed = "Failed",
  /** Deleted */
  Deleted = "Deleted",
  /** DeleteFailed */
  DeleteFailed = "DeleteFailed",
  /** CreateFailed */
  CreateFailed = "CreateFailed",
  /** UpdatedFailed */
  UpdatedFailed = "UpdatedFailed",
}

/**
 * Defines values for NamespaceProvisioningState. \
 * {@link KnownNamespaceProvisioningState} can be used interchangeably with NamespaceProvisioningState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Creating** \
 * **Updating** \
 * **Deleting** \
 * **Succeeded** \
 * **Canceled** \
 * **Failed** \
 * **Deleted** \
 * **DeleteFailed** \
 * **CreateFailed** \
 * **UpdatedFailed**
 */
export type NamespaceProvisioningState = string;

/** Known values of {@link CustomDomainValidationState} that the service accepts. */
export enum KnownCustomDomainValidationState {
  /** Pending */
  Pending = "Pending",
  /** Approved */
  Approved = "Approved",
  /** ErrorRetrievingDnsRecord */
  ErrorRetrievingDnsRecord = "ErrorRetrievingDnsRecord",
}

/**
 * Defines values for CustomDomainValidationState. \
 * {@link KnownCustomDomainValidationState} can be used interchangeably with CustomDomainValidationState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Pending** \
 * **Approved** \
 * **ErrorRetrievingDnsRecord**
 */
export type CustomDomainValidationState = string;

/** Known values of {@link CustomDomainIdentityType} that the service accepts. */
export enum KnownCustomDomainIdentityType {
  /** SystemAssigned */
  SystemAssigned = "SystemAssigned",
  /** UserAssigned */
  UserAssigned = "UserAssigned",
}

/**
 * Defines values for CustomDomainIdentityType. \
 * {@link KnownCustomDomainIdentityType} can be used interchangeably with CustomDomainIdentityType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **SystemAssigned** \
 * **UserAssigned**
 */
export type CustomDomainIdentityType = string;

/** Known values of {@link TopicSpacesConfigurationState} that the service accepts. */
export enum KnownTopicSpacesConfigurationState {
  /** Disabled */
  Disabled = "Disabled",
  /** Enabled */
  Enabled = "Enabled",
}

/**
 * Defines values for TopicSpacesConfigurationState. \
 * {@link KnownTopicSpacesConfigurationState} can be used interchangeably with TopicSpacesConfigurationState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Disabled** \
 * **Enabled**
 */
export type TopicSpacesConfigurationState = string;

/** Known values of {@link StaticRoutingEnrichmentType} that the service accepts. */
export enum KnownStaticRoutingEnrichmentType {
  /** String */
  String = "String",
}

/**
 * Defines values for StaticRoutingEnrichmentType. \
 * {@link KnownStaticRoutingEnrichmentType} can be used interchangeably with StaticRoutingEnrichmentType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **String**
 */
export type StaticRoutingEnrichmentType = string;

/** Known values of {@link RoutingIdentityType} that the service accepts. */
export enum KnownRoutingIdentityType {
  /** None */
  None = "None",
  /** SystemAssigned */
  SystemAssigned = "SystemAssigned",
  /** UserAssigned */
  UserAssigned = "UserAssigned",
}

/**
 * Defines values for RoutingIdentityType. \
 * {@link KnownRoutingIdentityType} can be used interchangeably with RoutingIdentityType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **None** \
 * **SystemAssigned** \
 * **UserAssigned**
 */
export type RoutingIdentityType = string;

/** Known values of {@link SkuName} that the service accepts. */
export enum KnownSkuName {
  /** Standard */
  Standard = "Standard",
}

/**
 * Defines values for SkuName. \
 * {@link KnownSkuName} can be used interchangeably with SkuName,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Standard**
 */
export type SkuName = string;

/** Known values of {@link NamespaceTopicProvisioningState} that the service accepts. */
export enum KnownNamespaceTopicProvisioningState {
  /** Creating */
  Creating = "Creating",
  /** Updating */
  Updating = "Updating",
  /** Deleting */
  Deleting = "Deleting",
  /** Succeeded */
  Succeeded = "Succeeded",
  /** Canceled */
  Canceled = "Canceled",
  /** Failed */
  Failed = "Failed",
  /** Deleted */
  Deleted = "Deleted",
  /** DeleteFailed */
  DeleteFailed = "DeleteFailed",
  /** CreateFailed */
  CreateFailed = "CreateFailed",
  /** UpdatedFailed */
  UpdatedFailed = "UpdatedFailed",
}

/**
 * Defines values for NamespaceTopicProvisioningState. \
 * {@link KnownNamespaceTopicProvisioningState} can be used interchangeably with NamespaceTopicProvisioningState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Creating** \
 * **Updating** \
 * **Deleting** \
 * **Succeeded** \
 * **Canceled** \
 * **Failed** \
 * **Deleted** \
 * **DeleteFailed** \
 * **CreateFailed** \
 * **UpdatedFailed**
 */
export type NamespaceTopicProvisioningState = string;

/** Known values of {@link PublisherType} that the service accepts. */
export enum KnownPublisherType {
  /** Custom */
  Custom = "Custom",
}

/**
 * Defines values for PublisherType. \
 * {@link KnownPublisherType} can be used interchangeably with PublisherType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Custom**
 */
export type PublisherType = string;

/** Known values of {@link EventInputSchema} that the service accepts. */
export enum KnownEventInputSchema {
  /** CloudEventSchemaV10 */
  CloudEventSchemaV10 = "CloudEventSchemaV1_0",
}

/**
 * Defines values for EventInputSchema. \
 * {@link KnownEventInputSchema} can be used interchangeably with EventInputSchema,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **CloudEventSchemaV1_0**
 */
export type EventInputSchema = string;

/** Known values of {@link TopicProvisioningState} that the service accepts. */
export enum KnownTopicProvisioningState {
  /** Creating */
  Creating = "Creating",
  /** Updating */
  Updating = "Updating",
  /** Deleting */
  Deleting = "Deleting",
  /** Succeeded */
  Succeeded = "Succeeded",
  /** Canceled */
  Canceled = "Canceled",
  /** Failed */
  Failed = "Failed",
}

/**
 * Defines values for TopicProvisioningState. \
 * {@link KnownTopicProvisioningState} can be used interchangeably with TopicProvisioningState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Creating** \
 * **Updating** \
 * **Deleting** \
 * **Succeeded** \
 * **Canceled** \
 * **Failed**
 */
export type TopicProvisioningState = string;

/** Known values of {@link PartnerConfigurationProvisioningState} that the service accepts. */
export enum KnownPartnerConfigurationProvisioningState {
  /** Creating */
  Creating = "Creating",
  /** Updating */
  Updating = "Updating",
  /** Deleting */
  Deleting = "Deleting",
  /** Succeeded */
  Succeeded = "Succeeded",
  /** Canceled */
  Canceled = "Canceled",
  /** Failed */
  Failed = "Failed",
}

/**
 * Defines values for PartnerConfigurationProvisioningState. \
 * {@link KnownPartnerConfigurationProvisioningState} can be used interchangeably with PartnerConfigurationProvisioningState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Creating** \
 * **Updating** \
 * **Deleting** \
 * **Succeeded** \
 * **Canceled** \
 * **Failed**
 */
export type PartnerConfigurationProvisioningState = string;

/** Known values of {@link PartnerNamespaceProvisioningState} that the service accepts. */
export enum KnownPartnerNamespaceProvisioningState {
  /** Creating */
  Creating = "Creating",
  /** Updating */
  Updating = "Updating",
  /** Deleting */
  Deleting = "Deleting",
  /** Succeeded */
  Succeeded = "Succeeded",
  /** Canceled */
  Canceled = "Canceled",
  /** Failed */
  Failed = "Failed",
}

/**
 * Defines values for PartnerNamespaceProvisioningState. \
 * {@link KnownPartnerNamespaceProvisioningState} can be used interchangeably with PartnerNamespaceProvisioningState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Creating** \
 * **Updating** \
 * **Deleting** \
 * **Succeeded** \
 * **Canceled** \
 * **Failed**
 */
export type PartnerNamespaceProvisioningState = string;

/** Known values of {@link PartnerTopicRoutingMode} that the service accepts. */
export enum KnownPartnerTopicRoutingMode {
  /** SourceEventAttribute */
  SourceEventAttribute = "SourceEventAttribute",
  /** ChannelNameHeader */
  ChannelNameHeader = "ChannelNameHeader",
}

/**
 * Defines values for PartnerTopicRoutingMode. \
 * {@link KnownPartnerTopicRoutingMode} can be used interchangeably with PartnerTopicRoutingMode,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **SourceEventAttribute** \
 * **ChannelNameHeader**
 */
export type PartnerTopicRoutingMode = string;

/** Known values of {@link PartnerRegistrationProvisioningState} that the service accepts. */
export enum KnownPartnerRegistrationProvisioningState {
  /** Creating */
  Creating = "Creating",
  /** Updating */
  Updating = "Updating",
  /** Deleting */
  Deleting = "Deleting",
  /** Succeeded */
  Succeeded = "Succeeded",
  /** Canceled */
  Canceled = "Canceled",
  /** Failed */
  Failed = "Failed",
}

/**
 * Defines values for PartnerRegistrationProvisioningState. \
 * {@link KnownPartnerRegistrationProvisioningState} can be used interchangeably with PartnerRegistrationProvisioningState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Creating** \
 * **Updating** \
 * **Deleting** \
 * **Succeeded** \
 * **Canceled** \
 * **Failed**
 */
export type PartnerRegistrationProvisioningState = string;

/** Known values of {@link PartnerTopicProvisioningState} that the service accepts. */
export enum KnownPartnerTopicProvisioningState {
  /** Creating */
  Creating = "Creating",
  /** Updating */
  Updating = "Updating",
  /** Deleting */
  Deleting = "Deleting",
  /** Succeeded */
  Succeeded = "Succeeded",
  /** Canceled */
  Canceled = "Canceled",
  /** Failed */
  Failed = "Failed",
  /** IdleDueToMirroredChannelResourceDeletion */
  IdleDueToMirroredChannelResourceDeletion = "IdleDueToMirroredChannelResourceDeletion",
}

/**
 * Defines values for PartnerTopicProvisioningState. \
 * {@link KnownPartnerTopicProvisioningState} can be used interchangeably with PartnerTopicProvisioningState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Creating** \
 * **Updating** \
 * **Deleting** \
 * **Succeeded** \
 * **Canceled** \
 * **Failed** \
 * **IdleDueToMirroredChannelResourceDeletion**
 */
export type PartnerTopicProvisioningState = string;

/** Known values of {@link PartnerTopicActivationState} that the service accepts. */
export enum KnownPartnerTopicActivationState {
  /** NeverActivated */
  NeverActivated = "NeverActivated",
  /** Activated */
  Activated = "Activated",
  /** Deactivated */
  Deactivated = "Deactivated",
}

/**
 * Defines values for PartnerTopicActivationState. \
 * {@link KnownPartnerTopicActivationState} can be used interchangeably with PartnerTopicActivationState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **NeverActivated** \
 * **Activated** \
 * **Deactivated**
 */
export type PartnerTopicActivationState = string;

/** Known values of {@link PermissionType} that the service accepts. */
export enum KnownPermissionType {
  /** Publisher */
  Publisher = "Publisher",
  /** Subscriber */
  Subscriber = "Subscriber",
}

/**
 * Defines values for PermissionType. \
 * {@link KnownPermissionType} can be used interchangeably with PermissionType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Publisher** \
 * **Subscriber**
 */
export type PermissionType = string;

/** Known values of {@link PermissionBindingProvisioningState} that the service accepts. */
export enum KnownPermissionBindingProvisioningState {
  /** Creating */
  Creating = "Creating",
  /** Updating */
  Updating = "Updating",
  /** Deleting */
  Deleting = "Deleting",
  /** Succeeded */
  Succeeded = "Succeeded",
  /** Canceled */
  Canceled = "Canceled",
  /** Failed */
  Failed = "Failed",
  /** Deleted */
  Deleted = "Deleted",
}

/**
 * Defines values for PermissionBindingProvisioningState. \
 * {@link KnownPermissionBindingProvisioningState} can be used interchangeably with PermissionBindingProvisioningState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Creating** \
 * **Updating** \
 * **Deleting** \
 * **Succeeded** \
 * **Canceled** \
 * **Failed** \
 * **Deleted**
 */
export type PermissionBindingProvisioningState = string;

/** Known values of {@link PrivateEndpointConnectionsParentType} that the service accepts. */
export enum KnownPrivateEndpointConnectionsParentType {
  /** Topics */
  Topics = "topics",
  /** Domains */
  Domains = "domains",
  /** PartnerNamespaces */
  PartnerNamespaces = "partnerNamespaces",
  /** Namespaces */
  Namespaces = "namespaces",
}

/**
 * Defines values for PrivateEndpointConnectionsParentType. \
 * {@link KnownPrivateEndpointConnectionsParentType} can be used interchangeably with PrivateEndpointConnectionsParentType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **topics** \
 * **domains** \
 * **partnerNamespaces** \
 * **namespaces**
 */
export type PrivateEndpointConnectionsParentType = string;

/** Known values of {@link TopicSpaceProvisioningState} that the service accepts. */
export enum KnownTopicSpaceProvisioningState {
  /** Creating */
  Creating = "Creating",
  /** Updating */
  Updating = "Updating",
  /** Deleting */
  Deleting = "Deleting",
  /** Succeeded */
  Succeeded = "Succeeded",
  /** Canceled */
  Canceled = "Canceled",
  /** Failed */
  Failed = "Failed",
  /** Deleted */
  Deleted = "Deleted",
}

/**
 * Defines values for TopicSpaceProvisioningState. \
 * {@link KnownTopicSpaceProvisioningState} can be used interchangeably with TopicSpaceProvisioningState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Creating** \
 * **Updating** \
 * **Deleting** \
 * **Succeeded** \
 * **Canceled** \
 * **Failed** \
 * **Deleted**
 */
export type TopicSpaceProvisioningState = string;

/** Known values of {@link ResourceRegionType} that the service accepts. */
export enum KnownResourceRegionType {
  /** RegionalResource */
  RegionalResource = "RegionalResource",
  /** GlobalResource */
  GlobalResource = "GlobalResource",
}

/**
 * Defines values for ResourceRegionType. \
 * {@link KnownResourceRegionType} can be used interchangeably with ResourceRegionType,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **RegionalResource** \
 * **GlobalResource**
 */
export type ResourceRegionType = string;

/** Known values of {@link TopicTypeProvisioningState} that the service accepts. */
export enum KnownTopicTypeProvisioningState {
  /** Creating */
  Creating = "Creating",
  /** Updating */
  Updating = "Updating",
  /** Deleting */
  Deleting = "Deleting",
  /** Succeeded */
  Succeeded = "Succeeded",
  /** Canceled */
  Canceled = "Canceled",
  /** Failed */
  Failed = "Failed",
}

/**
 * Defines values for TopicTypeProvisioningState. \
 * {@link KnownTopicTypeProvisioningState} can be used interchangeably with TopicTypeProvisioningState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Creating** \
 * **Updating** \
 * **Deleting** \
 * **Succeeded** \
 * **Canceled** \
 * **Failed**
 */
export type TopicTypeProvisioningState = string;

/** Known values of {@link TopicTypeSourceScope} that the service accepts. */
export enum KnownTopicTypeSourceScope {
  /** Resource */
  Resource = "Resource",
  /** ResourceGroup */
  ResourceGroup = "ResourceGroup",
  /** AzureSubscription */
  AzureSubscription = "AzureSubscription",
  /** ManagementGroup */
  ManagementGroup = "ManagementGroup",
}

/**
 * Defines values for TopicTypeSourceScope. \
 * {@link KnownTopicTypeSourceScope} can be used interchangeably with TopicTypeSourceScope,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Resource** \
 * **ResourceGroup** \
 * **AzureSubscription** \
 * **ManagementGroup**
 */
export type TopicTypeSourceScope = string;

/** Known values of {@link VerifiedPartnerProvisioningState} that the service accepts. */
export enum KnownVerifiedPartnerProvisioningState {
  /** Creating */
  Creating = "Creating",
  /** Updating */
  Updating = "Updating",
  /** Deleting */
  Deleting = "Deleting",
  /** Succeeded */
  Succeeded = "Succeeded",
  /** Canceled */
  Canceled = "Canceled",
  /** Failed */
  Failed = "Failed",
}

/**
 * Defines values for VerifiedPartnerProvisioningState. \
 * {@link KnownVerifiedPartnerProvisioningState} can be used interchangeably with VerifiedPartnerProvisioningState,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Creating** \
 * **Updating** \
 * **Deleting** \
 * **Succeeded** \
 * **Canceled** \
 * **Failed**
 */
export type VerifiedPartnerProvisioningState = string;

/** Known values of {@link MonitorAlertSeverity} that the service accepts. */
export enum KnownMonitorAlertSeverity {
  /** Sev0 */
  Sev0 = "Sev0",
  /** Sev1 */
  Sev1 = "Sev1",
  /** Sev2 */
  Sev2 = "Sev2",
  /** Sev3 */
  Sev3 = "Sev3",
  /** Sev4 */
  Sev4 = "Sev4",
}

/**
 * Defines values for MonitorAlertSeverity. \
 * {@link KnownMonitorAlertSeverity} can be used interchangeably with MonitorAlertSeverity,
 *  this enum contains the known values that the service supports.
 * ### Known values supported by the service
 * **Sev0** \
 * **Sev1** \
 * **Sev2** \
 * **Sev3** \
 * **Sev4**
 */
export type MonitorAlertSeverity = string;

/** Optional parameters. */
export interface CaCertificatesGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type CaCertificatesGetResponse = CaCertificate;

/** Optional parameters. */
export interface CaCertificatesCreateOrUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the createOrUpdate operation. */
export type CaCertificatesCreateOrUpdateResponse = CaCertificate;

/** Optional parameters. */
export interface CaCertificatesDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface CaCertificatesListByNamespaceOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listByNamespace operation. */
export type CaCertificatesListByNamespaceResponse = CaCertificatesListResult;

/** Optional parameters. */
export interface CaCertificatesListByNamespaceNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listByNamespaceNext operation. */
export type CaCertificatesListByNamespaceNextResponse =
  CaCertificatesListResult;

/** Optional parameters. */
export interface ChannelsGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type ChannelsGetResponse = Channel;

/** Optional parameters. */
export interface ChannelsCreateOrUpdateOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the createOrUpdate operation. */
export type ChannelsCreateOrUpdateResponse = Channel;

/** Optional parameters. */
export interface ChannelsDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface ChannelsUpdateOptionalParams
  extends coreClient.OperationOptions {}

/** Optional parameters. */
export interface ChannelsListByPartnerNamespaceOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listByPartnerNamespace operation. */
export type ChannelsListByPartnerNamespaceResponse = ChannelsListResult;

/** Optional parameters. */
export interface ChannelsGetFullUrlOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getFullUrl operation. */
export type ChannelsGetFullUrlResponse = EventSubscriptionFullUrl;

/** Optional parameters. */
export interface ChannelsListByPartnerNamespaceNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listByPartnerNamespaceNext operation. */
export type ChannelsListByPartnerNamespaceNextResponse = ChannelsListResult;

/** Optional parameters. */
export interface ClientGroupsGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type ClientGroupsGetResponse = ClientGroup;

/** Optional parameters. */
export interface ClientGroupsCreateOrUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the createOrUpdate operation. */
export type ClientGroupsCreateOrUpdateResponse = ClientGroup;

/** Optional parameters. */
export interface ClientGroupsDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface ClientGroupsListByNamespaceOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listByNamespace operation. */
export type ClientGroupsListByNamespaceResponse = ClientGroupsListResult;

/** Optional parameters. */
export interface ClientGroupsListByNamespaceNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listByNamespaceNext operation. */
export type ClientGroupsListByNamespaceNextResponse = ClientGroupsListResult;

/** Optional parameters. */
export interface ClientsGetOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type ClientsGetResponse = Client;

/** Optional parameters. */
export interface ClientsCreateOrUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the createOrUpdate operation. */
export type ClientsCreateOrUpdateResponse = Client;

/** Optional parameters. */
export interface ClientsDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface ClientsListByNamespaceOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listByNamespace operation. */
export type ClientsListByNamespaceResponse = ClientsListResult;

/** Optional parameters. */
export interface ClientsListByNamespaceNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listByNamespaceNext operation. */
export type ClientsListByNamespaceNextResponse = ClientsListResult;

/** Optional parameters. */
export interface DomainsGetOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type DomainsGetResponse = Domain;

/** Optional parameters. */
export interface DomainsCreateOrUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the createOrUpdate operation. */
export type DomainsCreateOrUpdateResponse = Domain;

/** Optional parameters. */
export interface DomainsDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface DomainsUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface DomainsListBySubscriptionOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listBySubscription operation. */
export type DomainsListBySubscriptionResponse = DomainsListResult;

/** Optional parameters. */
export interface DomainsListByResourceGroupOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listByResourceGroup operation. */
export type DomainsListByResourceGroupResponse = DomainsListResult;

/** Optional parameters. */
export interface DomainsListSharedAccessKeysOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listSharedAccessKeys operation. */
export type DomainsListSharedAccessKeysResponse = DomainSharedAccessKeys;

/** Optional parameters. */
export interface DomainsRegenerateKeyOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the regenerateKey operation. */
export type DomainsRegenerateKeyResponse = DomainSharedAccessKeys;

/** Optional parameters. */
export interface DomainsListBySubscriptionNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listBySubscriptionNext operation. */
export type DomainsListBySubscriptionNextResponse = DomainsListResult;

/** Optional parameters. */
export interface DomainsListByResourceGroupNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listByResourceGroupNext operation. */
export type DomainsListByResourceGroupNextResponse = DomainsListResult;

/** Optional parameters. */
export interface DomainTopicsGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type DomainTopicsGetResponse = DomainTopic;

/** Optional parameters. */
export interface DomainTopicsCreateOrUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the createOrUpdate operation. */
export type DomainTopicsCreateOrUpdateResponse = DomainTopic;

/** Optional parameters. */
export interface DomainTopicsDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface DomainTopicsListByDomainOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listByDomain operation. */
export type DomainTopicsListByDomainResponse = DomainTopicsListResult;

/** Optional parameters. */
export interface DomainTopicsListByDomainNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listByDomainNext operation. */
export type DomainTopicsListByDomainNextResponse = DomainTopicsListResult;

/** Optional parameters. */
export interface TopicEventSubscriptionsGetDeliveryAttributesOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getDeliveryAttributes operation. */
export type TopicEventSubscriptionsGetDeliveryAttributesResponse =
  DeliveryAttributeListResult;

/** Optional parameters. */
export interface TopicEventSubscriptionsGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type TopicEventSubscriptionsGetResponse = EventSubscription;

/** Optional parameters. */
export interface TopicEventSubscriptionsCreateOrUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the createOrUpdate operation. */
export type TopicEventSubscriptionsCreateOrUpdateResponse = EventSubscription;

/** Optional parameters. */
export interface TopicEventSubscriptionsDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface TopicEventSubscriptionsUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the update operation. */
export type TopicEventSubscriptionsUpdateResponse = EventSubscription;

/** Optional parameters. */
export interface TopicEventSubscriptionsGetFullUrlOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getFullUrl operation. */
export type TopicEventSubscriptionsGetFullUrlResponse =
  EventSubscriptionFullUrl;

/** Optional parameters. */
export interface TopicEventSubscriptionsListOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the list operation. */
export type TopicEventSubscriptionsListResponse = EventSubscriptionsListResult;

/** Optional parameters. */
export interface TopicEventSubscriptionsListNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listNext operation. */
export type TopicEventSubscriptionsListNextResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface DomainEventSubscriptionsGetDeliveryAttributesOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getDeliveryAttributes operation. */
export type DomainEventSubscriptionsGetDeliveryAttributesResponse =
  DeliveryAttributeListResult;

/** Optional parameters. */
export interface DomainEventSubscriptionsGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type DomainEventSubscriptionsGetResponse = EventSubscription;

/** Optional parameters. */
export interface DomainEventSubscriptionsCreateOrUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the createOrUpdate operation. */
export type DomainEventSubscriptionsCreateOrUpdateResponse = EventSubscription;

/** Optional parameters. */
export interface DomainEventSubscriptionsDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface DomainEventSubscriptionsUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the update operation. */
export type DomainEventSubscriptionsUpdateResponse = EventSubscription;

/** Optional parameters. */
export interface DomainEventSubscriptionsGetFullUrlOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getFullUrl operation. */
export type DomainEventSubscriptionsGetFullUrlResponse =
  EventSubscriptionFullUrl;

/** Optional parameters. */
export interface DomainEventSubscriptionsListOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the list operation. */
export type DomainEventSubscriptionsListResponse = EventSubscriptionsListResult;

/** Optional parameters. */
export interface DomainEventSubscriptionsListNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listNext operation. */
export type DomainEventSubscriptionsListNextResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface NamespaceTopicEventSubscriptionsGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type NamespaceTopicEventSubscriptionsGetResponse = Subscription;

/** Optional parameters. */
export interface NamespaceTopicEventSubscriptionsCreateOrUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the createOrUpdate operation. */
export type NamespaceTopicEventSubscriptionsCreateOrUpdateResponse =
  Subscription;

/** Optional parameters. */
export interface NamespaceTopicEventSubscriptionsDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface NamespaceTopicEventSubscriptionsUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the update operation. */
export type NamespaceTopicEventSubscriptionsUpdateResponse = Subscription;

/** Optional parameters. */
export interface NamespaceTopicEventSubscriptionsListByNamespaceTopicOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listByNamespaceTopic operation. */
export type NamespaceTopicEventSubscriptionsListByNamespaceTopicResponse =
  SubscriptionsListResult;

/** Optional parameters. */
export interface NamespaceTopicEventSubscriptionsGetDeliveryAttributesOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getDeliveryAttributes operation. */
export type NamespaceTopicEventSubscriptionsGetDeliveryAttributesResponse =
  DeliveryAttributeListResult;

/** Optional parameters. */
export interface NamespaceTopicEventSubscriptionsGetFullUrlOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getFullUrl operation. */
export type NamespaceTopicEventSubscriptionsGetFullUrlResponse =
  SubscriptionFullUrl;

/** Optional parameters. */
export interface NamespaceTopicEventSubscriptionsListByNamespaceTopicNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listByNamespaceTopicNext operation. */
export type NamespaceTopicEventSubscriptionsListByNamespaceTopicNextResponse =
  SubscriptionsListResult;

/** Optional parameters. */
export interface EventSubscriptionsGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type EventSubscriptionsGetResponse = EventSubscription;

/** Optional parameters. */
export interface EventSubscriptionsCreateOrUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the createOrUpdate operation. */
export type EventSubscriptionsCreateOrUpdateResponse = EventSubscription;

/** Optional parameters. */
export interface EventSubscriptionsDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface EventSubscriptionsUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the update operation. */
export type EventSubscriptionsUpdateResponse = EventSubscription;

/** Optional parameters. */
export interface EventSubscriptionsGetFullUrlOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getFullUrl operation. */
export type EventSubscriptionsGetFullUrlResponse = EventSubscriptionFullUrl;

/** Optional parameters. */
export interface EventSubscriptionsListGlobalBySubscriptionOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listGlobalBySubscription operation. */
export type EventSubscriptionsListGlobalBySubscriptionResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface EventSubscriptionsListGlobalBySubscriptionForTopicTypeOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listGlobalBySubscriptionForTopicType operation. */
export type EventSubscriptionsListGlobalBySubscriptionForTopicTypeResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface EventSubscriptionsListGlobalByResourceGroupOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listGlobalByResourceGroup operation. */
export type EventSubscriptionsListGlobalByResourceGroupResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface EventSubscriptionsListGlobalByResourceGroupForTopicTypeOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listGlobalByResourceGroupForTopicType operation. */
export type EventSubscriptionsListGlobalByResourceGroupForTopicTypeResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface EventSubscriptionsListRegionalBySubscriptionOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listRegionalBySubscription operation. */
export type EventSubscriptionsListRegionalBySubscriptionResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface EventSubscriptionsListRegionalByResourceGroupOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listRegionalByResourceGroup operation. */
export type EventSubscriptionsListRegionalByResourceGroupResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface EventSubscriptionsListRegionalBySubscriptionForTopicTypeOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listRegionalBySubscriptionForTopicType operation. */
export type EventSubscriptionsListRegionalBySubscriptionForTopicTypeResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface EventSubscriptionsListRegionalByResourceGroupForTopicTypeOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listRegionalByResourceGroupForTopicType operation. */
export type EventSubscriptionsListRegionalByResourceGroupForTopicTypeResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface EventSubscriptionsListByResourceOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listByResource operation. */
export type EventSubscriptionsListByResourceResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface EventSubscriptionsListByDomainTopicOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listByDomainTopic operation. */
export type EventSubscriptionsListByDomainTopicResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface EventSubscriptionsGetDeliveryAttributesOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getDeliveryAttributes operation. */
export type EventSubscriptionsGetDeliveryAttributesResponse =
  DeliveryAttributeListResult;

/** Optional parameters. */
export interface EventSubscriptionsListGlobalBySubscriptionNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listGlobalBySubscriptionNext operation. */
export type EventSubscriptionsListGlobalBySubscriptionNextResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface EventSubscriptionsListGlobalBySubscriptionForTopicTypeNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listGlobalBySubscriptionForTopicTypeNext operation. */
export type EventSubscriptionsListGlobalBySubscriptionForTopicTypeNextResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface EventSubscriptionsListGlobalByResourceGroupNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listGlobalByResourceGroupNext operation. */
export type EventSubscriptionsListGlobalByResourceGroupNextResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface EventSubscriptionsListGlobalByResourceGroupForTopicTypeNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listGlobalByResourceGroupForTopicTypeNext operation. */
export type EventSubscriptionsListGlobalByResourceGroupForTopicTypeNextResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface EventSubscriptionsListRegionalBySubscriptionNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listRegionalBySubscriptionNext operation. */
export type EventSubscriptionsListRegionalBySubscriptionNextResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface EventSubscriptionsListRegionalByResourceGroupNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listRegionalByResourceGroupNext operation. */
export type EventSubscriptionsListRegionalByResourceGroupNextResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface EventSubscriptionsListRegionalBySubscriptionForTopicTypeNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listRegionalBySubscriptionForTopicTypeNext operation. */
export type EventSubscriptionsListRegionalBySubscriptionForTopicTypeNextResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface EventSubscriptionsListRegionalByResourceGroupForTopicTypeNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listRegionalByResourceGroupForTopicTypeNext operation. */
export type EventSubscriptionsListRegionalByResourceGroupForTopicTypeNextResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface EventSubscriptionsListByResourceNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listByResourceNext operation. */
export type EventSubscriptionsListByResourceNextResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface EventSubscriptionsListByDomainTopicNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listByDomainTopicNext operation. */
export type EventSubscriptionsListByDomainTopicNextResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface DomainTopicEventSubscriptionsGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type DomainTopicEventSubscriptionsGetResponse = EventSubscription;

/** Optional parameters. */
export interface DomainTopicEventSubscriptionsCreateOrUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the createOrUpdate operation. */
export type DomainTopicEventSubscriptionsCreateOrUpdateResponse =
  EventSubscription;

/** Optional parameters. */
export interface DomainTopicEventSubscriptionsDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface DomainTopicEventSubscriptionsUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the update operation. */
export type DomainTopicEventSubscriptionsUpdateResponse = EventSubscription;

/** Optional parameters. */
export interface DomainTopicEventSubscriptionsGetFullUrlOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getFullUrl operation. */
export type DomainTopicEventSubscriptionsGetFullUrlResponse =
  EventSubscriptionFullUrl;

/** Optional parameters. */
export interface DomainTopicEventSubscriptionsListOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the list operation. */
export type DomainTopicEventSubscriptionsListResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface DomainTopicEventSubscriptionsGetDeliveryAttributesOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getDeliveryAttributes operation. */
export type DomainTopicEventSubscriptionsGetDeliveryAttributesResponse =
  DeliveryAttributeListResult;

/** Optional parameters. */
export interface DomainTopicEventSubscriptionsListNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listNext operation. */
export type DomainTopicEventSubscriptionsListNextResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface SystemTopicEventSubscriptionsGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type SystemTopicEventSubscriptionsGetResponse = EventSubscription;

/** Optional parameters. */
export interface SystemTopicEventSubscriptionsCreateOrUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the createOrUpdate operation. */
export type SystemTopicEventSubscriptionsCreateOrUpdateResponse =
  EventSubscription;

/** Optional parameters. */
export interface SystemTopicEventSubscriptionsDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface SystemTopicEventSubscriptionsUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the update operation. */
export type SystemTopicEventSubscriptionsUpdateResponse = EventSubscription;

/** Optional parameters. */
export interface SystemTopicEventSubscriptionsGetFullUrlOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getFullUrl operation. */
export type SystemTopicEventSubscriptionsGetFullUrlResponse =
  EventSubscriptionFullUrl;

/** Optional parameters. */
export interface SystemTopicEventSubscriptionsListBySystemTopicOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listBySystemTopic operation. */
export type SystemTopicEventSubscriptionsListBySystemTopicResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface SystemTopicEventSubscriptionsGetDeliveryAttributesOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getDeliveryAttributes operation. */
export type SystemTopicEventSubscriptionsGetDeliveryAttributesResponse =
  DeliveryAttributeListResult;

/** Optional parameters. */
export interface SystemTopicEventSubscriptionsListBySystemTopicNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listBySystemTopicNext operation. */
export type SystemTopicEventSubscriptionsListBySystemTopicNextResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface PartnerTopicEventSubscriptionsGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type PartnerTopicEventSubscriptionsGetResponse = EventSubscription;

/** Optional parameters. */
export interface PartnerTopicEventSubscriptionsCreateOrUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the createOrUpdate operation. */
export type PartnerTopicEventSubscriptionsCreateOrUpdateResponse =
  EventSubscription;

/** Optional parameters. */
export interface PartnerTopicEventSubscriptionsDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface PartnerTopicEventSubscriptionsUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the update operation. */
export type PartnerTopicEventSubscriptionsUpdateResponse = EventSubscription;

/** Optional parameters. */
export interface PartnerTopicEventSubscriptionsGetFullUrlOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getFullUrl operation. */
export type PartnerTopicEventSubscriptionsGetFullUrlResponse =
  EventSubscriptionFullUrl;

/** Optional parameters. */
export interface PartnerTopicEventSubscriptionsListByPartnerTopicOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listByPartnerTopic operation. */
export type PartnerTopicEventSubscriptionsListByPartnerTopicResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface PartnerTopicEventSubscriptionsGetDeliveryAttributesOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the getDeliveryAttributes operation. */
export type PartnerTopicEventSubscriptionsGetDeliveryAttributesResponse =
  DeliveryAttributeListResult;

/** Optional parameters. */
export interface PartnerTopicEventSubscriptionsListByPartnerTopicNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listByPartnerTopicNext operation. */
export type PartnerTopicEventSubscriptionsListByPartnerTopicNextResponse =
  EventSubscriptionsListResult;

/** Optional parameters. */
export interface NamespacesGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type NamespacesGetResponse = Namespace;

/** Optional parameters. */
export interface NamespacesCreateOrUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the createOrUpdate operation. */
export type NamespacesCreateOrUpdateResponse = Namespace;

/** Optional parameters. */
export interface NamespacesDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface NamespacesUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the update operation. */
export type NamespacesUpdateResponse = Namespace;

/** Optional parameters. */
export interface NamespacesListBySubscriptionOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listBySubscription operation. */
export type NamespacesListBySubscriptionResponse = NamespacesListResult;

/** Optional parameters. */
export interface NamespacesListByResourceGroupOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listByResourceGroup operation. */
export type NamespacesListByResourceGroupResponse = NamespacesListResult;

/** Optional parameters. */
export interface NamespacesListSharedAccessKeysOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listSharedAccessKeys operation. */
export type NamespacesListSharedAccessKeysResponse = NamespaceSharedAccessKeys;

/** Optional parameters. */
export interface NamespacesRegenerateKeyOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the regenerateKey operation. */
export type NamespacesRegenerateKeyResponse = NamespaceSharedAccessKeys;

/** Optional parameters. */
export interface NamespacesValidateCustomDomainOwnershipOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the validateCustomDomainOwnership operation. */
export type NamespacesValidateCustomDomainOwnershipResponse =
  CustomDomainOwnershipValidationResult;

/** Optional parameters. */
export interface NamespacesListBySubscriptionNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listBySubscriptionNext operation. */
export type NamespacesListBySubscriptionNextResponse = NamespacesListResult;

/** Optional parameters. */
export interface NamespacesListByResourceGroupNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listByResourceGroupNext operation. */
export type NamespacesListByResourceGroupNextResponse = NamespacesListResult;

/** Optional parameters. */
export interface NamespaceTopicsGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type NamespaceTopicsGetResponse = NamespaceTopic;

/** Optional parameters. */
export interface NamespaceTopicsCreateOrUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the createOrUpdate operation. */
export type NamespaceTopicsCreateOrUpdateResponse = NamespaceTopic;

/** Optional parameters. */
export interface NamespaceTopicsDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface NamespaceTopicsUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the update operation. */
export type NamespaceTopicsUpdateResponse = NamespaceTopic;

/** Optional parameters. */
export interface NamespaceTopicsListByNamespaceOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listByNamespace operation. */
export type NamespaceTopicsListByNamespaceResponse = NamespaceTopicsListResult;

/** Optional parameters. */
export interface NamespaceTopicsListSharedAccessKeysOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listSharedAccessKeys operation. */
export type NamespaceTopicsListSharedAccessKeysResponse = TopicSharedAccessKeys;

/** Optional parameters. */
export interface NamespaceTopicsRegenerateKeyOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the regenerateKey operation. */
export type NamespaceTopicsRegenerateKeyResponse = TopicSharedAccessKeys;

/** Optional parameters. */
export interface NamespaceTopicsListByNamespaceNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listByNamespaceNext operation. */
export type NamespaceTopicsListByNamespaceNextResponse =
  NamespaceTopicsListResult;

/** Optional parameters. */
export interface OperationsListOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the list operation. */
export type OperationsListResponse = OperationsListResult;

/** Optional parameters. */
export interface TopicsGetOptionalParams extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type TopicsGetResponse = Topic;

/** Optional parameters. */
export interface TopicsCreateOrUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the createOrUpdate operation. */
export type TopicsCreateOrUpdateResponse = Topic;

/** Optional parameters. */
export interface TopicsDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the delete operation. */
export type TopicsDeleteResponse = TopicsDeleteHeaders;

/** Optional parameters. */
export interface TopicsUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface TopicsListBySubscriptionOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listBySubscription operation. */
export type TopicsListBySubscriptionResponse = TopicsListResult;

/** Optional parameters. */
export interface TopicsListByResourceGroupOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listByResourceGroup operation. */
export type TopicsListByResourceGroupResponse = TopicsListResult;

/** Optional parameters. */
export interface TopicsListSharedAccessKeysOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listSharedAccessKeys operation. */
export type TopicsListSharedAccessKeysResponse = TopicSharedAccessKeys;

/** Optional parameters. */
export interface TopicsRegenerateKeyOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the regenerateKey operation. */
export type TopicsRegenerateKeyResponse = TopicSharedAccessKeys;

/** Optional parameters. */
export interface TopicsListEventTypesOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listEventTypes operation. */
export type TopicsListEventTypesResponse = EventTypesListResult;

/** Optional parameters. */
export interface TopicsListBySubscriptionNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listBySubscriptionNext operation. */
export type TopicsListBySubscriptionNextResponse = TopicsListResult;

/** Optional parameters. */
export interface TopicsListByResourceGroupNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listByResourceGroupNext operation. */
export type TopicsListByResourceGroupNextResponse = TopicsListResult;

/** Optional parameters. */
export interface PartnerConfigurationsGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type PartnerConfigurationsGetResponse = PartnerConfiguration;

/** Optional parameters. */
export interface PartnerConfigurationsCreateOrUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the createOrUpdate operation. */
export type PartnerConfigurationsCreateOrUpdateResponse = PartnerConfiguration;

/** Optional parameters. */
export interface PartnerConfigurationsDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface PartnerConfigurationsUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the update operation. */
export type PartnerConfigurationsUpdateResponse = PartnerConfiguration;

/** Optional parameters. */
export interface PartnerConfigurationsListByResourceGroupOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listByResourceGroup operation. */
export type PartnerConfigurationsListByResourceGroupResponse =
  PartnerConfigurationsListResult;

/** Optional parameters. */
export interface PartnerConfigurationsListBySubscriptionOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listBySubscription operation. */
export type PartnerConfigurationsListBySubscriptionResponse =
  PartnerConfigurationsListResult;

/** Optional parameters. */
export interface PartnerConfigurationsAuthorizePartnerOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the authorizePartner operation. */
export type PartnerConfigurationsAuthorizePartnerResponse =
  PartnerConfiguration;

/** Optional parameters. */
export interface PartnerConfigurationsUnauthorizePartnerOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the unauthorizePartner operation. */
export type PartnerConfigurationsUnauthorizePartnerResponse =
  PartnerConfiguration;

/** Optional parameters. */
export interface PartnerConfigurationsListBySubscriptionNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listBySubscriptionNext operation. */
export type PartnerConfigurationsListBySubscriptionNextResponse =
  PartnerConfigurationsListResult;

/** Optional parameters. */
export interface PartnerNamespacesGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type PartnerNamespacesGetResponse = PartnerNamespace;

/** Optional parameters. */
export interface PartnerNamespacesCreateOrUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the createOrUpdate operation. */
export type PartnerNamespacesCreateOrUpdateResponse = PartnerNamespace;

/** Optional parameters. */
export interface PartnerNamespacesDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface PartnerNamespacesUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface PartnerNamespacesListBySubscriptionOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listBySubscription operation. */
export type PartnerNamespacesListBySubscriptionResponse =
  PartnerNamespacesListResult;

/** Optional parameters. */
export interface PartnerNamespacesListByResourceGroupOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listByResourceGroup operation. */
export type PartnerNamespacesListByResourceGroupResponse =
  PartnerNamespacesListResult;

/** Optional parameters. */
export interface PartnerNamespacesListSharedAccessKeysOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listSharedAccessKeys operation. */
export type PartnerNamespacesListSharedAccessKeysResponse =
  PartnerNamespaceSharedAccessKeys;

/** Optional parameters. */
export interface PartnerNamespacesRegenerateKeyOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the regenerateKey operation. */
export type PartnerNamespacesRegenerateKeyResponse =
  PartnerNamespaceSharedAccessKeys;

/** Optional parameters. */
export interface PartnerNamespacesListBySubscriptionNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listBySubscriptionNext operation. */
export type PartnerNamespacesListBySubscriptionNextResponse =
  PartnerNamespacesListResult;

/** Optional parameters. */
export interface PartnerNamespacesListByResourceGroupNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listByResourceGroupNext operation. */
export type PartnerNamespacesListByResourceGroupNextResponse =
  PartnerNamespacesListResult;

/** Optional parameters. */
export interface PartnerRegistrationsGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type PartnerRegistrationsGetResponse = PartnerRegistration;

/** Optional parameters. */
export interface PartnerRegistrationsCreateOrUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the createOrUpdate operation. */
export type PartnerRegistrationsCreateOrUpdateResponse = PartnerRegistration;

/** Optional parameters. */
export interface PartnerRegistrationsDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface PartnerRegistrationsUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface PartnerRegistrationsListBySubscriptionOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listBySubscription operation. */
export type PartnerRegistrationsListBySubscriptionResponse =
  PartnerRegistrationsListResult;

/** Optional parameters. */
export interface PartnerRegistrationsListByResourceGroupOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listByResourceGroup operation. */
export type PartnerRegistrationsListByResourceGroupResponse =
  PartnerRegistrationsListResult;

/** Optional parameters. */
export interface PartnerRegistrationsListBySubscriptionNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listBySubscriptionNext operation. */
export type PartnerRegistrationsListBySubscriptionNextResponse =
  PartnerRegistrationsListResult;

/** Optional parameters. */
export interface PartnerRegistrationsListByResourceGroupNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listByResourceGroupNext operation. */
export type PartnerRegistrationsListByResourceGroupNextResponse =
  PartnerRegistrationsListResult;

/** Optional parameters. */
export interface PartnerTopicsGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type PartnerTopicsGetResponse = PartnerTopic;

/** Optional parameters. */
export interface PartnerTopicsCreateOrUpdateOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the createOrUpdate operation. */
export type PartnerTopicsCreateOrUpdateResponse = PartnerTopic;

/** Optional parameters. */
export interface PartnerTopicsDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface PartnerTopicsUpdateOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the update operation. */
export type PartnerTopicsUpdateResponse = PartnerTopic;

/** Optional parameters. */
export interface PartnerTopicsListBySubscriptionOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listBySubscription operation. */
export type PartnerTopicsListBySubscriptionResponse = PartnerTopicsListResult;

/** Optional parameters. */
export interface PartnerTopicsListByResourceGroupOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listByResourceGroup operation. */
export type PartnerTopicsListByResourceGroupResponse = PartnerTopicsListResult;

/** Optional parameters. */
export interface PartnerTopicsActivateOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the activate operation. */
export type PartnerTopicsActivateResponse = PartnerTopic;

/** Optional parameters. */
export interface PartnerTopicsDeactivateOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the deactivate operation. */
export type PartnerTopicsDeactivateResponse = PartnerTopic;

/** Optional parameters. */
export interface PartnerTopicsListBySubscriptionNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listBySubscriptionNext operation. */
export type PartnerTopicsListBySubscriptionNextResponse =
  PartnerTopicsListResult;

/** Optional parameters. */
export interface PartnerTopicsListByResourceGroupNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listByResourceGroupNext operation. */
export type PartnerTopicsListByResourceGroupNextResponse =
  PartnerTopicsListResult;

/** Optional parameters. */
export interface PermissionBindingsGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type PermissionBindingsGetResponse = PermissionBinding;

/** Optional parameters. */
export interface PermissionBindingsCreateOrUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the createOrUpdate operation. */
export type PermissionBindingsCreateOrUpdateResponse = PermissionBinding;

/** Optional parameters. */
export interface PermissionBindingsDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface PermissionBindingsListByNamespaceOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listByNamespace operation. */
export type PermissionBindingsListByNamespaceResponse =
  PermissionBindingsListResult;

/** Optional parameters. */
export interface PermissionBindingsListByNamespaceNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listByNamespaceNext operation. */
export type PermissionBindingsListByNamespaceNextResponse =
  PermissionBindingsListResult;

/** Optional parameters. */
export interface PrivateEndpointConnectionsGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type PrivateEndpointConnectionsGetResponse = PrivateEndpointConnection;

/** Optional parameters. */
export interface PrivateEndpointConnectionsUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the update operation. */
export type PrivateEndpointConnectionsUpdateResponse =
  PrivateEndpointConnection;

/** Optional parameters. */
export interface PrivateEndpointConnectionsDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the delete operation. */
export type PrivateEndpointConnectionsDeleteResponse =
  PrivateEndpointConnectionsDeleteHeaders;

/** Optional parameters. */
export interface PrivateEndpointConnectionsListByResourceOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listByResource operation. */
export type PrivateEndpointConnectionsListByResourceResponse =
  PrivateEndpointConnectionListResult;

/** Optional parameters. */
export interface PrivateEndpointConnectionsListByResourceNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listByResourceNext operation. */
export type PrivateEndpointConnectionsListByResourceNextResponse =
  PrivateEndpointConnectionListResult;

/** Optional parameters. */
export interface PrivateLinkResourcesGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type PrivateLinkResourcesGetResponse = PrivateLinkResource;

/** Optional parameters. */
export interface PrivateLinkResourcesListByResourceOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listByResource operation. */
export type PrivateLinkResourcesListByResourceResponse =
  PrivateLinkResourcesListResult;

/** Optional parameters. */
export interface PrivateLinkResourcesListByResourceNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listByResourceNext operation. */
export type PrivateLinkResourcesListByResourceNextResponse =
  PrivateLinkResourcesListResult;

/** Optional parameters. */
export interface SystemTopicsGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type SystemTopicsGetResponse = SystemTopic;

/** Optional parameters. */
export interface SystemTopicsCreateOrUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the createOrUpdate operation. */
export type SystemTopicsCreateOrUpdateResponse = SystemTopic;

/** Optional parameters. */
export interface SystemTopicsDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface SystemTopicsUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the update operation. */
export type SystemTopicsUpdateResponse = SystemTopic;

/** Optional parameters. */
export interface SystemTopicsListBySubscriptionOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listBySubscription operation. */
export type SystemTopicsListBySubscriptionResponse = SystemTopicsListResult;

/** Optional parameters. */
export interface SystemTopicsListByResourceGroupOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listByResourceGroup operation. */
export type SystemTopicsListByResourceGroupResponse = SystemTopicsListResult;

/** Optional parameters. */
export interface SystemTopicsListBySubscriptionNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listBySubscriptionNext operation. */
export type SystemTopicsListBySubscriptionNextResponse = SystemTopicsListResult;

/** Optional parameters. */
export interface SystemTopicsListByResourceGroupNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listByResourceGroupNext operation. */
export type SystemTopicsListByResourceGroupNextResponse =
  SystemTopicsListResult;

/** Optional parameters. */
export interface ExtensionTopicsGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type ExtensionTopicsGetResponse = ExtensionTopic;

/** Optional parameters. */
export interface TopicSpacesGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type TopicSpacesGetResponse = TopicSpace;

/** Optional parameters. */
export interface TopicSpacesCreateOrUpdateOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Contains response data for the createOrUpdate operation. */
export type TopicSpacesCreateOrUpdateResponse = TopicSpace;

/** Optional parameters. */
export interface TopicSpacesDeleteOptionalParams
  extends coreClient.OperationOptions {
  /** Delay to wait until next poll, in milliseconds. */
  updateIntervalInMs?: number;
  /** A serialized poller which can be used to resume an existing paused Long-Running-Operation. */
  resumeFrom?: string;
}

/** Optional parameters. */
export interface TopicSpacesListByNamespaceOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the listByNamespace operation. */
export type TopicSpacesListByNamespaceResponse = TopicSpacesListResult;

/** Optional parameters. */
export interface TopicSpacesListByNamespaceNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listByNamespaceNext operation. */
export type TopicSpacesListByNamespaceNextResponse = TopicSpacesListResult;

/** Optional parameters. */
export interface TopicTypesListOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the list operation. */
export type TopicTypesListResponse = TopicTypesListResult;

/** Optional parameters. */
export interface TopicTypesGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type TopicTypesGetResponse = TopicTypeInfo;

/** Optional parameters. */
export interface TopicTypesListEventTypesOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listEventTypes operation. */
export type TopicTypesListEventTypesResponse = EventTypesListResult;

/** Optional parameters. */
export interface VerifiedPartnersGetOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the get operation. */
export type VerifiedPartnersGetResponse = VerifiedPartner;

/** Optional parameters. */
export interface VerifiedPartnersListOptionalParams
  extends coreClient.OperationOptions {
  /** The query used to filter the search results using OData syntax. Filtering is permitted on the 'name' property only and with limited number of OData operations. These operations are: the 'contains' function as well as the following logical operations: not, and, or, eq (for equal), and ne (for not equal). No arithmetic operations are supported. The following is a valid filter example: $filter=contains(namE, 'PATTERN') and name ne 'PATTERN-1'. The following is not a valid filter example: $filter=location eq 'westus'. */
  filter?: string;
  /** The number of results to return per page for the list operation. Valid range for top parameter is 1 to 100. If not specified, the default number of results to be returned is 20 items per page. */
  top?: number;
}

/** Contains response data for the list operation. */
export type VerifiedPartnersListResponse = VerifiedPartnersListResult;

/** Optional parameters. */
export interface VerifiedPartnersListNextOptionalParams
  extends coreClient.OperationOptions {}

/** Contains response data for the listNext operation. */
export type VerifiedPartnersListNextResponse = VerifiedPartnersListResult;

/** Optional parameters. */
export interface EventGridManagementClientOptionalParams
  extends coreClient.ServiceClientOptions {
  /** server parameter */
  $host?: string;
  /** Api Version */
  apiVersion?: string;
  /** Overrides client endpoint. */
  endpoint?: string;
}
